import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ModalBasic from "@/components/ModalBasic.jsx";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
  SecondaryDangerButton,
} from "@/components/Buttons.jsx";
import { banCustomer } from "@/api/customers.js";
import InlineLoader from "@/components/InlineLoader.jsx";
import CustomerDetailModal from "@/components/CustomerDetailModal.jsx";
import {
  acceptOrder,
  cancelOrder,
  getSubstitutionRecommendation,
  completeOrder,
  deleteItemFromCart,
  getCartByOrderID,
  getOrderByID,
  markOrderAsReturned,
  rejectOrder,
  saveOrderUpdate,
  updateOrderCart,
} from "@/api/index.js";
import { getActiveOrders } from "@/store/orders/action.js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  findMerchantLocationByStoreId,
  formatPhoneNumber,
  formatValue,
  OrderStatusInverseEnum,
  statusColor,
  GetCardIcon,
} from "@/utils/Utils.js";
import {
  FaMagnifyingGlass,
  FaCircleCheck,
  FaTriangleExclamation,
  FaCircleXmark,
  FaCircleInfo,
  FaTruck,
  FaDollarSign,
  FaList,
  FaComment,
  FaArrowUpRightFromSquare,
  FaBell,
  FaCrown,
  FaTag,
  FaClock,
} from "react-icons/fa6";
import { PiDetectiveBold } from "react-icons/pi";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import {
  InstantSearch,
  Configure,
  useRefinementList,
} from "react-instantsearch-hooks";
import config from "@/config";
import { addToast, setSelectedMerchant } from "@/store/app/action.js";
import moment from "moment";
import Tooltip from "@/components/Tooltip.jsx";
import Badge from "@/components/Badges.jsx";

import { useHits, useSearchBox } from "react-instantsearch-hooks";
import Autocomplete from "../products/autocomplete.jsx";
import { useSmallSize } from "@/utils/UseSmallSize.jsx";
import CanView from "@/components/CanView.jsx";
import PaymentModal from "./PaymentModal.jsx";
import { getStableFakeName } from "@/utils/fakeName.js";
import OrderHistoryTimeline from "@/components/orders/OrderHistoryTimeline.jsx";
const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
  server: {
    apiKey: config.ts.key, // Be sure to use an API key that only allows search operations
    nodes: config.ts.nodes,
    nearestNode: config.ts.nearestNode,
    cacheSearchResultsForSeconds: 2 * 60, // Cache search results from server. Defaults to 2 minutes. Set to 0 to disable caching.
  },
  // The following parameters are directly passed to Typesense's search API endpoint.
  //  So you can pass any parameters supported by the search endpoint below.
  //  query_by is required.
  additionalSearchParameters: {
    query_by: "title,subtitle,description,default_category,company",
    groupBy: "sku_id",
    groupLimit: 99,
  },
});
const searchClient = typesenseInstantsearchAdapter.searchClient;

const substitutionPreferenceMap = {
  REPLACE: "receive substitute",
  REMOVE: "skip item",
  CONTACT: "be suggested alternative",
};
const CustomRefinementList = ({ isTutorial = false }) => {
  const isSmallSize = useSmallSize();
  const [expanded, setExpanded] = useState(!isSmallSize);
  useEffect(() => {
    if (isSmallSize) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [isSmallSize]);

  let { items: categoryItems, refine: categoryRefine } = useRefinementList({
    attribute: "default_category",
    sortBy: ["count"],
  });

  let { items: brandItems, refine: brandRefine } = useRefinementList({
    attribute: "company",
    sortBy: ["count"],
  });

  if (isTutorial) {
    categoryItems = [
      {
        label: "Essentials",
      },
    ];
    brandItems = [
      {
        label: "Tutorial Items",
      },
    ];
    categoryRefine = (value) => { };
    brandRefine = (value) => { };
  }

  return (
    <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
      <div
        className={`flex flex-col gap-2 h-full ${isSmallSize ? "p-2" : "p-5"}`}
      >
        {isSmallSize && (
          <div className="flex items-center justify-between font-bold">
            Filters
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white"
            >
              {expanded ? "Hide" : "Show"}
            </button>
          </div>
        )}
        <div
          className={"flex flex-col h-full " + (expanded ? "grow" : "hidden")}
        >
          <header>
            <div className="flex items-center justify-between font-bold">
              Categories
            </div>
          </header>
          <div className="grow mt-2">
            {categoryItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between py-2"
              >
                {/*checkbox*/}
                <input
                  type="checkbox"
                  id={item.label}
                  name={item.label}
                  value={item.label}
                  checked={item.isRefined}
                  onChange={() => {
                    categoryRefine(item.value);
                  }}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                />
                <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                  {item.label}
                </span>
                <div className="text-sm font-medium text-slate-500">
                  {item.count}
                </div>
              </div>
            ))}
          </div>

          <header>
            <div className="flex items-center justify-between mt-8 font-bold">
              Brands
            </div>
          </header>
          <div className="grow mt-2">
            {brandItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between py-2"
              >
                {/*checkbox*/}
                <input
                  type="checkbox"
                  id={item.label}
                  name={item.label}
                  value={item.label}
                  checked={item.isRefined}
                  onChange={() => {
                    brandRefine(item.value);
                  }}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                />
                <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                  {item.label}
                </span>
                <div className="text-sm font-medium text-slate-500">
                  {item.count}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CancelModalContent = ({
  cancelReason,
  setCancelReason,
  customCancelReason,
  setCustomCancelReason,
  setCancelError,
}) => {
  return (
    <>
      <div className="space-y-4">
        <div>Are you sure you want to cancel this order?</div>
        <div>
          The customer will be refunded in full if you proceed. This action is
          not reversible.
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Reason for Cancellation *
          </label>
          <select
            id="cancel-reason-select"
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              setCustomCancelReason("");
              setCancelError("");
            }}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-slate-100"
          >
            <option value="">Select a reason</option>
            <option value="out_of_stock">Out of Stock</option>
            <option value="customer_request">Customer Request</option>
            <option value="payment_issue">Payment Issue</option>
            <option value="delivery_issue">Delivery Issue</option>
            <option value="operational_issue">Operational Issue</option>
            <option value="product_unavailable">Product Unavailable</option>
            <option value="delay_in_processing">Delay in Processing</option>
            <option value="fraud">Fraudulant Activity (Ban User)</option>
            <option value="other">Other</option>
          </select>
        </div>

        {cancelReason === "other" ? (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Please specify the reason *
            </label>
            <input
              type="text"
              value={customCancelReason}
              onChange={(e) => {
                setCustomCancelReason(e.target.value);
                setCancelError("");
              }}
              className="form-input w-full"
              placeholder="Enter the reason for this cancellation"
              maxLength={255}
            />
          </div>
        ) : null}

        {cancelReason === "fraud" ? (
          <div>
            <label className="block text-sm font-medium text-red-500">
              Canceling this order with a fraudulant activity will ban the user
              from your stores. Are you sure you want to proceed?
            </label>
          </div>
        ) : null}
      </div>
    </>
  );
};

function deliveryDropOff(detail) {
  if (!detail) return {};
  const drop = {};
  if (detail.city) drop.city = detail.city;
  if (detail.state) drop.state = detail.state;
  if (detail.zip) drop.zip = detail.zip;
  if (detail.county) drop.county = detail.county;
  if (detail.lat != null) drop.lat = detail.lat;
  if (detail.lng != null) drop.lng = detail.lng;
  return drop;
}

const OrderModal = ({
  setModalOpen,
  modalOpen,
  orderID,
  tutorialRunning,
  tutorialStepIndex,
  tutorialNextStep,
  setTutorialOrder,
}) => {
  const [forceReload, setForceReload] = useState(null);
  const { lastUpdated } = useSelector((state) => state.orders);
  const [myLastUpdated, setMyLastUpdated] = useState(lastUpdated);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [order, setOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [addingProduct, setAddingProduct] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cart, setCart] = useState(null);
  const isSmallSize = useSmallSize();
  const { selectedLocation, selectedMerchant, merchants, user, isDemoUser } =
    useSelector((state) => state.app);
  const tutorialOrders = useSelector((state) => state.orders.tutorialOrders);
  const [notifications, setNotifications] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [additionalModal, setAdditionalModal] = useState(false);
  const [modalConfirmation, setModalConfirmation] = useState({});
  const addProductButton = useRef(null);
  const [currentView, setCurrentView] = useState("order");
  const [show3DSWarning, setShow3DSWarning] = useState(false);
  const [viewPaymentIntentId, setViewPaymentIntentId] = useState(null);
  const [originalOrderTotal, setOriginalOrderTotal] = useState(0);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [lineItems, setLineItems] = useState(null);
  const [oosModalOpen, setOosModalOpen] = useState(false);
  const [oosProduct, setOosProduct] = useState(null);
  const [oosOrderProduct, setOosOrderProduct] = useState(null);
  const [oosRecommendedProducts, setOosRecommendedProducts] = useState([]);
  const [oosSubstitutedProduct, setOosSubstitutedProduct] = useState(null);
  const [oosAvailableQuantity, setOosAvailableQuantity] = useState(0);
  const [oosApiRecommendations, setOosApiRecommendations] = useState([]);
  const [oosLoadingRecommendations, setOosLoadingRecommendations] =
    useState(false);
  const [oosSelectedRecommendations, setOosSelectedRecommendations] = useState(
    []
  );
  const [oosCurrentPage, setOosCurrentPage] = useState(0);
  const [oosItemsPerPage] = useState(9); // Show 9 items per page
  const [oosFlow, setOosFlow] = useState(false);
  const [oosSelectionsMap, setOosSelectionsMap] = useState({}); // Persistent OOS selections by product sku
  const [oosSummaryOpen, setOosSummaryOpen] = useState(false); // Show summary before final API call
  const [cancelReason, setCancelReason] = useState("");
  const [customCancelReason, setCustomCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [viewFraudEval, setViewFraudEval] = useState(null);

  // Close fraud modal when main modal closes


  const _completeOrder = async (force = false) => {
    try {
      await completeOrder(order._id, force);

      setAdditionalModal(false);
      setProcessing(false);
      setTimeout(() => {
        setModalOpen(false);
      }, 250);
    } catch (error) {
      if (error.response) {
        if (error.response.data?.error?.raw?.message) {
          setAdditionalModal({
            action: () => {
              setProcessing(true);
              _completeOrder("override");
            }, // Dummy action since we handle it in confirm button
            title: "Payment Error",
            titleBarClassName: "bg-red-400",
            holdToContinue: true,
            danger: true,
            actionText: "Override Payment Error",
            content: <div><p>An error has occured trying to capture payment for this order, do not complete.</p><p>
              If you want to force complete the order by overriding the payment error, click the button below. </p><p><strong> Note you may not be paid for this order if you proceed.</strong></p></div>
          });

        } else {
          dispatch(addToast({ type: "error", message: "An error occurred, completing the order, please try again." }));
        }
      }
    } finally {
      setProcessing(false);
    }
  }
  useEffect(() => {
    if (!modalOpen) {
      setViewFraudEval(null);
    }
  }, [modalOpen]);
  // Reset cancel form helper
  const resetCancelForm = () => {
    setCancelReason("");
    setCustomCancelReason("");
    setCancelError("");
  };

  // Format time remaining for countdown display
  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return "Time expired";

    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!editingOrder && orderID && lastUpdated !== myLastUpdated) {
      if (lastUpdated - myLastUpdated < 10000) {
        return;
      }
      setMyLastUpdated(lastUpdated);
      setForceReload(Date.now());
    }
  }, [myLastUpdated, lastUpdated, orderID, editingOrder]);

  // Countdown timer for customer decision time
  useEffect(() => {
    if (!order?.recommend_start || !order?.recommend_min) {
      setTimeRemaining(null);
      return;
    }

    const startTime = new Date(order.recommend_start).getTime();
    const durationMs = order.recommend_min * 60 * 1000; // Convert minutes to milliseconds
    const endTime = startTime + durationMs;

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = endTime - now;

      if (remaining <= 0) {
        setTimeRemaining(0);
        return;
      }

      setTimeRemaining(remaining);
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [order?.recommend_start, order?.recommend_min]);

  const ageConfirmed = useMemo(() => {
    return Boolean(modalConfirmation?.ageConfirmed);
  }, [modalConfirmation]);

  const addToCart = async (product) => {
    const products = cart.products;
    const payload = {
      ...cart,
      products: products.concat({ ...product, qty: 1 }),
    };

    await updateCart(payload);
  };
  const deleteItem = async (sku, skipCall) => {
    const products = cart.products;
    const product = products.find((p) => p.sku_id === sku);

    product.qty = "";

    if (skipCall) {
      setCart({
        ...cart,
        products: products,
      });
      return;
    }

    let body = {
      skus: [sku],
      key: editCartKey,
      storeId: order.store_id,
      tip: order.tip_amount,
      orderType: order.order_type,
      employeeDelivery: order.employee_delivery,
      checkout: true,
      user_id: order.customer.parse_id,
    };
    setProcessing(true);
    const res = await deleteItemFromCart(body);
    setCart(res);
    setProcessing(false);
  };

  const calculateAge = useCallback(
    (yyyy, mm, dd) => {
      const year = yyyy;
      const month = mm;
      const day = dd;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dob = new Date(year, month - 1, day); // JavaScript's months are 0-indexed
      const diff_ms = today - dob.getTime();
      const age_dt = new Date(diff_ms);
      const age = Math.abs(age_dt.getFullYear() - 1970);

      if (age >= 21) {
        setModalConfirmation({ ...modalConfirmation, ageConfirmed: true });
      } else {
        setModalConfirmation({ ...modalConfirmation, ageConfirmed: false });
      }
    },
    [modalConfirmation]
  );

  const editQuantity = async (sku, qty) => {
    let products = cart.products;
    let product = products.find((p) => p.sku_id === sku);
    let skipCall = false;
    if (qty === "") {
      skipCall = true;
      qty = "0";
    }
    product.qty = qty;

    if (!skipCall && qty === "0") {
      products = products.filter((p) => p.sku_id !== sku);
    }
    const payload = {
      ...cart,
      products: products,
    };

    if (skipCall) {
      return;
    }
    await updateCart(payload);
  };

  const saveOrder = async () => {
    const payload = {
      _id: order._id,
      key: editCartKey,
      tip: order.tip_amount,
      orderType: order.order_type,
      employeeDelivery: order.employee_delivery,
      storeId: order.store_id,
    };
    setProcessing(true);
    try {
      await saveOrderUpdate(order._id, payload);
      setHasUnsavedChanges(false);
      setForceReload(Date.now());
      setEditingOrder(false);
    } catch (e) {
      let message = "An error occurred";
      try {
        const serverMessage = e.response?.data?.message;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch { }
      message += ", updates are NOT saved.";
      dispatch(
        addToast({
          type: "error",
          message: message,
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleOutOfStock = async (product, orderProduct) => {
    setOosProduct(product);
    setOosOrderProduct(orderProduct);
    setOosModalOpen(true);

    // Load existing selection if any
    const existingSelection = loadOosSelection(product.sku_id);
    if (existingSelection) {
      setOosAvailableQuantity(existingSelection.availableQuantity);
      setOosSubstitutedProduct(existingSelection.substitutedProduct);
      setOosSelectedRecommendations(
        existingSelection.selectedRecommendations || []
      );
    } else {
      // Reset to defaults
      const orderedQuantity = product?.qty ?? product?.quantity ?? 0;
      setOosAvailableQuantity(Math.max(0, orderedQuantity - 1)); // Default to n-1 (short by 1)
      setOosSubstitutedProduct(null);
      setOosSelectedRecommendations([]);
      setOosCurrentPage(0);
    }

    setOosApiRecommendations([]);
    setOosRecommendedProducts([]);

    // Fetch substitution recommendations for CONTACT or REPLACE preferences
    if (
      orderProduct?.substitution_preference === "CONTACT" ||
      orderProduct?.substitution_preference === "REPLACE"
    ) {
      setOosLoadingRecommendations(true);
      try {
        const response = await getSubstitutionRecommendation(
          order.store_id,
          product.sku_id
        );
        const recommendations = response?.substitutes || [];
        setOosApiRecommendations(recommendations);
      } catch (error) {
        setOosApiRecommendations([]);
      } finally {
        setOosLoadingRecommendations(false);
      }
    }
  };

  // Pagination helpers
  const recommendations = Array.isArray(oosApiRecommendations)
    ? oosApiRecommendations
    : [];
  const totalPages = Math.ceil(recommendations.length / oosItemsPerPage);
  const paginatedRecommendations = recommendations.slice(
    oosCurrentPage * oosItemsPerPage,
    (oosCurrentPage + 1) * oosItemsPerPage
  );

  // Selection handlers
  const handleRecommendationSelect = (item, isSelected) => {
    if (oosOrderProduct?.substitution_preference === "REPLACE") {
      // For REPLACE: only one selection allowed
      setOosSelectedRecommendations(isSelected ? [item] : []);
      setOosSubstitutedProduct(isSelected ? item : null);
    } else if (oosOrderProduct?.substitution_preference === "CONTACT") {
      // For CONTACT: up to 5 selections allowed
      setOosSelectedRecommendations((prev) => {
        if (isSelected) {
          return [...prev, item];
        } else {
          return prev.filter((selected) => selected.sku_id !== item.sku_id);
        }
      });
    }
  };

  const isRecommendationSelected = (item) => {
    return oosSelectedRecommendations.some(
      (selected) => selected.sku_id === item.sku_id
    );
  };

  const getMaxSelections = () => {
    return oosOrderProduct?.substitution_preference === "REPLACE" ? 1 : 5;
  };

  // Price delta helper
  const getPriceDelta = (recommendationPrice) => {
    if (!oosProduct?.price && !oosProduct?.sell_price) return null;
    const originalPrice = oosProduct.price || oosProduct.sell_price;
    const delta = recommendationPrice - originalPrice;
    return {
      amount: Math.abs(delta),
      isPositive: delta > 0,
      isNegative: delta < 0,
      isSame: delta === 0,
    };
  };

  // OOS Selections Management
  const saveOosSelection = (skuId, selection) => {
    setOosSelectionsMap((prev) => ({
      ...prev,
      [skuId]: selection,
    }));
  };

  const loadOosSelection = (skuId) => {
    return oosSelectionsMap[skuId] || null;
  };

  const hasOosSelection = (skuId) => {
    return skuId in oosSelectionsMap && oosSelectionsMap[skuId] !== null;
  };

  // Clear all OOS-related state when modal closes
  const clearOosState = () => {
    setOosSelectionsMap({});
    setOosProduct(null);
    setOosOrderProduct(null);
    setOosRecommendedProducts([]);
    setOosSubstitutedProduct(null);
    setOosAvailableQuantity(0);
    setOosApiRecommendations([]);
    setOosSelectedRecommendations([]);
    setOosCurrentPage(0);
    setOosModalOpen(false);
    setOosSummaryOpen(false);
    setOosFlow(false);
  };

  const handleConfirmOOS = () => {
    // Save the current selection to persistent state
    const selection = {
      availableQuantity: oosAvailableQuantity,
      substitutedProduct: oosSubstitutedProduct,
      selectedRecommendations: oosSelectedRecommendations,
      timestamp: Date.now(),
    };

    saveOosSelection(oosProduct.sku_id, selection);
    // Close the modal and proceed to continue button flow
    setOosModalOpen(false);
  };

  // Build the rejection API payload
  const buildRejectionPayload = () => {
    const payload = {
      params: {
        order: {
          objectId: order._id,
        },
        quantities: {},
        substitutions: [],
        recommendations: {},
      },
    };

    // Add quantities for all selected OOS items
    order.products.forEach((product) => {
      const skuId = product.sku_id;
      const selection = oosSelectionsMap[skuId];
      const orderedQuantity =
        order?.products?.find((p) => p.sku_id === skuId)?.quantity || 0;

      if (selection) {
        payload.params.quantities[skuId] = selection.availableQuantity;

        // Handle substitutions (REPLACE preference)
        if (selection.substitutedProduct) {
          // Calculate how many items need to be substituted
          const substitutionQty = orderedQuantity - selection.availableQuantity;

          if (substitutionQty > 0) {
            payload.params.substitutions.push({
              sku: selection.substitutedProduct.sku_id,
              qty: substitutionQty,
              original_sku: skuId,
            });
          }
        }

        // Handle recommendations (CONTACT preference)
        if (selection.selectedRecommendations.length > 0) {
          // Calculate how many items need to be replaced (shortfall)
          const replacementQty = orderedQuantity - selection.availableQuantity;

          payload.params.recommendations[skuId] = {
            options: selection.selectedRecommendations.map((rec) => rec.sku_id),
            qty: replacementQty,
          };
        }
      } else {
        payload.params.quantities[skuId] = orderedQuantity;
      }
    });

    return payload;
  };

  // Handle final submission from summary screen
  const handleFinalSubmission = async () => {
    const payload = buildRejectionPayload();
    try {
      setProcessing(true);
      await rejectOrder(payload);
      setOosFlow(false);
      setOosSummaryOpen(false);
      setModalOpen(false);
      dispatch(getActiveOrders(true));
    } catch (error) {
      setProcessing(false);
    }
  };
  const updateCart = async (payload) => {
    if (tutorialRunning && tutorialStepIndex === 6) {
      tutorialNextStep();
    }
    if (order.tutorial) {
      payload.products = payload.products
        .map((p) => {
          if (p.qty !== undefined) {
            p.quantity = p.qty;
          }
          if (!p.price) {
            p.price = p[order.store_id + "_price"];
          }
          return p;
        })
        .filter((p) => p.quantity);
      dispatch({
        type: "TUTORIAL_PRODUCT_UPDATE",
        payload: { order_id: order._id, order: payload },
      });
      setForceReload(Date.now());
      return;
    }
    setProcessing(true);

    const skus = payload.products.map((product) => ({
      sku: product.sku_id,
      qty: product.qty,
      store_id: order.store_id,
    }));

    let body = {
      skus: skus,
      key: editCartKey,
      storeId: order.store_id,
      tip: order.tip_amount,
      orderType: order.order_type,
      employeeDelivery: order.employee_delivery,
      user_id: order.customer.parse_id,
    };
    if (order.order_type === "delivery") {
      Object.assign(body, deliveryDropOff(order.delivery_address_detail));
    }

    const res = await updateOrderCart(body);
    setCart(res);
    setHasUnsavedChanges(true);
    setProcessing(false);
  };

  const editCartKey = useMemo(() => {
    if (!order) {
      return "";
    }
    if (order.direct) {
      return [
        "cart:v2:direct-order",
        order._id,
        order.store_id,
        Date.now(),
      ].join("/");
    } else {
      return [
        "cart:v2:order",
        order.customer.parse_id,
        order._id,
        Date.now(),
      ].join("/");
    }
  }, [order]);

  useEffect(() => {
    if (additionalModal === "x") {
      setAdditionalModal(false);
      setModalOpen(false);
    }
    if (!additionalModal) {
      setCancelReason("");
      setCustomCancelReason("");
    }
  }, [additionalModal]);

  // Clear OOS state when modal closes
  useEffect(() => {
    if (!modalOpen) {
      clearOosState();
    }
  }, [modalOpen]);
  useEffect(() => {
    setAdditionalModal(false);
    if (!modalOpen) {
      setEditingOrder(null);
      setAddingProduct(false);
      setModalConfirmation({});
      setForceReload(null);
      setCurrentView("order");
      setOrder(null);
      setProcessing(false);
      setLoading(false);
      setProcessing(false);
      setCart(null);
      setLineItems(null);
      resetCancelForm();
    }
  }, [modalOpen]);

  const filter = useMemo(() => {
    if (!selectedLocation || !editingOrder) return "";
    // exclude skus of editingOrder's product
    let filters = [`${selectedLocation.store_id}_price:>0`];
    if (editingOrder.products.length > 0) {
      const excludeSkus = editingOrder.products.map((p) => p.sku_id).join(",");
      filters.push(`sku_id:!=[${excludeSkus}]`);
    }
    filters.push(`${selectedLocation.store_id}_qty:>0`);
    return `${filters.join(" && ")}`;
  }, [selectedLocation, editingOrder]);

  useEffect(() => {
    if (!editingOrder) setAddingProduct(false);
  }, [editingOrder]);
  useEffect(() => {
    if (tutorialRunning && tutorialStepIndex === 5 && addingProduct) {
      tutorialNextStep();
    }
  }, [addingProduct, tutorialRunning, tutorialStepIndex]);
  useEffect(() => {
    if (!addingProduct && addProductButton.current) {
      addProductButton.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [addingProduct]);
  useEffect(() => {
    dispatch({ type: "RESET_UNSEEN_ORDER_COUNT" });
    if (orderID.startsWith("tutorial-")) {
      let _order = tutorialOrders.find((order) => order._id === orderID);
      setOrder(_order);
      setCart(_order);
      setTutorialOrder(_order);
      return;
    }
    if (orderID) {
      setLoading(true);
      // fetch order details
      getOrderByID(orderID)
        .then(async (order) => {
          const res = order.order;
          const foundStore = findMerchantLocationByStoreId(
            merchants,
            res.store_id
          );
          if (
            foundStore &&
            res.store_id != null &&
            String(selectedLocation?.store_id ?? "") !==
            String(res.store_id)
          ) {
            await dispatch(setSelectedMerchant(foundStore.merchant.id));
            dispatch({
              type: "SET_SELECTED_LOCATION",
              payload: foundStore.location.id,
            });
            setForceReload(Date.now());
            return;
          }
          if ([3, 5].includes(res.order_status)) {
            navigate(`/past-orders?id=${orderID}`);
            return;
          }
          const notifications = order.notifications;

          if (isDemoUser && !res.direct) {
            const { first_name, last_name } = getStableFakeName(
              res._id,
              !res.direct
            );
            res.customer.first_name = first_name;
            res.customer.last_name = last_name;
          }


          setOrder(res);
          setNotifications(notifications);
          setEditingOrder(null);
          setAddingProduct(false);
          setAdditionalModal(false);
          setModalConfirmation({});
          setOriginalOrderTotal(res.total);

          let queries = {};
          if (res.order_type === "delivery") {
            queries = deliveryDropOff(res.delivery_address_detail);
          }
          if (res.tip_amount) {
            queries.tip = res.tip_amount;
          }
          queries.orderType = res.order_type;
          queries.employeeDelivery = res.employee_delivery;
          if (!res.direct) {
            queries.mp = true;
          }
          await getCartByOrderID(
            res._id,
            order.order.customer.parse_id,
            queries
          ).then(async (cart) => {
            if (!cart) {
              cart = {};
            }
            if (!cart?.products?.length) {
              const skus = res.products.map((product) => ({
                sku: product.sku_id,
                qty: product.quantity,
                store_id: res.store_id,
              }));

              let tempUpdateKey = "";
              if (res.direct) {
                tempUpdateKey = [
                  "cart:v2:direct-order",
                  res._id,
                  res.store_id,
                  Date.now(),
                ].join("/");
              } else {
                tempUpdateKey = [
                  "cart:v2:order",
                  res.customer.parse_id,
                  res._id,
                  Date.now(),
                ].join("/");
              }

              let body = {
                skus: skus,
                key: tempUpdateKey,
                storeId: res.store_id,
                tip: res.tip_amount,
                orderType: res.order_type,
                user_id: res.customer.parse_id,
              };
              if (order.order_type === "delivery") {
                Object.assign(body, deliveryDropOff(res.delivery_address_detail));
              }

              const newRes = await updateOrderCart(body, true);
              const payload = {
                _id: res._id,
                key: tempUpdateKey,
                tip: res.tip_amount,
                orderType: res.order_type,
                storeId: res.store_id,
              };
              await saveOrderUpdate(res._id, payload, true);
              cart = newRes;


            }
            if (!res.direct) {
              // if (cart && Array.isArray(cart.line_items) && Array.isArray(order.line_items)) {
              //   // Create a shallow copy so we can delete matched line_items from order.line_items
              //   const orderLineItems = [...order.line_items];
              //   const updatedCartLineItems = cart.line_items.map((cartItem) => {
              //     // Find index of matching item by title in order.line_items
              //     const matchIdx = orderLineItems.findIndex(
              //       (orderItem) => orderItem.title === cartItem.title
              //     );
              //     if (matchIdx !== -1) {
              //       const matchedOrderItem = orderLineItems[matchIdx];
              //       // Remove the matched order line_item to avoid duplicate replacements
              //       orderLineItems.splice(matchIdx, 1);
              //       return matchedOrderItem;
              //     }
              //     return cartItem;
              //   });
              //   cart.line_items = updatedCartLineItems;
              //   // Also update order.line_items to reflect removals
              //   order.line_items = orderLineItems;
              // }
              const mpLineItems = order.line_items;
              setLineItems(mpLineItems);
            }


            setCart(cart);
          });
        })
        .finally(() => {
          setLoading(false);
          setProcessing(false);
          setEditingOrder(null);
        });
    }
  }, [orderID, forceReload, merchants]);

  const withholding = useMemo(() => {
    return (
      <div className="flex flex-col w-full">
        <span className="font-bold">Application fee:</span>
        {[...((order?.direct ? cart?.line_items : []) || []), ...(lineItems || [])]?.map((item) => {
          let classname = `flex`;
          if (item.title.includes("Total")) classname += ` font-bold`;
          if (!item.withholding) {
            return;
          }

          return (
            <div key={"cart_" + item.title} className={classname}>
              <div className="flex gap-2 items-center flex-grow">
                {item.title}
              </div>
              <div className="w-24 justify-end flex">
                {formatValue(item.value)}
              </div>
            </div>
          );
        })}
        {lineItems?.map}
      </div>
    );
  }, [cart, lineItems]);
  const ageModal = useMemo(() => {
    return (
      <>
        <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {/* Payment Information */}
          {order?.payment_card_last_4 && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-400 dark:text-slate-500">
                Payment:
              </span>
              <div className="flex items-center gap-1">
                <img
                  src={GetCardIcon({
                    brand: order?.payment_card_brand?.toLowerCase(),
                  })}
                  alt={order?.payment_card_brand}
                  className="h-5 w-auto"
                />
                <span className="text-sm font-mono font-bold text-gray-800 dark:text-gray-200">
                  •••• {order?.payment_card_last_4}
                </span>
              </div>
            </div>
          )}

          {/* Customer Name */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-400 dark:text-slate-500">
              Name:
            </span>
            <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
              {order?.customer?.first_name} {order?.customer?.last_name}
            </span>
          </div>

          {/* Date of Birth with Verification Status */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-400 dark:text-slate-500">
              DOB:
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
                {order?.customer?.birthday}
              </span>
              {order?.customer?.verification_status === "PASSED" ? (
                <Badge slim color={"green"}>
                  Verified
                </Badge>
              ) : (
                <Badge slim color={"red"}>
                  Self-reported
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div>Please check customer's ID and confirm age below.</div>
        <div>
          <form>
            <div className={"space-x-2"}>
              <input
                id={"dob"}
                autoFocus={true}
                type="number"
                className="form-input w-16"
                placeholder="YYYY"
                onChange={(e) => {
                  const year = e.target.value;
                  if (year.length === 4) {
                    const currentYear = new Date().getFullYear();
                    const age = currentYear - year;

                    const monthInput = e.target.nextElementSibling;
                    const dayInput = monthInput.nextElementSibling;
                    if (age > 21) {
                      setModalConfirmation({
                        ...modalConfirmation,
                        ageConfirmed: true,
                      });
                      monthInput.disabled = true;
                      dayInput.disabled = true;
                    } else {
                      monthInput.disabled = false;
                      dayInput.disabled = false;
                      monthInput.focus();
                      if (monthInput.value && dayInput.value) {
                        calculateAge(year, monthInput.value, dayInput.value);
                      } else {
                        setModalConfirmation({
                          ...modalConfirmation,
                          ageConfirmed: false,
                        });
                      }
                    }
                  } else {
                    setModalConfirmation({
                      ...modalConfirmation,
                      ageConfirmed: false,
                    });
                  }
                }}
              />
              <input
                type="number"
                className="form-input w-16"
                placeholder="MM"
                onChange={(e) => {
                  let month = e.target.value;
                  if (month.length === 1 && month !== "0" && month !== "1") {
                    month = "0" + month; // Prefix a '0' if the month is a single digit (2-9)
                    e.target.value = month; // Set the corrected value back to the input
                  }

                  if (month.length === 2) {
                    const yearInput = e.target.previousElementSibling;
                    const dayInput = e.target.nextElementSibling;
                    e.target.nextElementSibling.focus();
                    if (yearInput.value && dayInput.value) {
                      calculateAge(yearInput.value, month, dayInput.value);
                    }
                  }
                }}
              />
              <input
                type="number"
                className="form-input w-16"
                placeholder="DD"
                onChange={(e) => {
                  if (e.target.value.length <= 2 && e.target.value.length > 0) {
                    const yearInput =
                      e.target.previousElementSibling.previousElementSibling;
                    const monthInput = e.target.previousElementSibling;
                    if (yearInput.value && monthInput.value) {
                      calculateAge(
                        yearInput.value,
                        monthInput.value,
                        e.target.value
                      );
                    }
                  }
                }}
              />
            </div>
          </form>
        </div>
      </>
    );
  }, [ageConfirmed, calculateAge, modalConfirmation]);

  const PrimaryActionButton = additionalModal?.danger
    ? DangerButton
    : PrimaryButton;
  return (
    <div>
      <ModalBasic
        width={"3xl"}
        className={"max-h-[90vh]"}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        title={
          loading
            ? "Loading"
            : "Order Details - " +
            (order?.direct
              ? order?.order_number
              : order?.easy_id + " (" + order?.order_number + ")")
        }
      >
        {loading || !order ? (
          <InlineLoader message="Please wait..." />
        ) : (
          <div
            className={`flex flex-col justify-center items-center __tutorial-order-modal_container ${isSmallSize ? "m-2 gap-2" : "m-4 gap-5"
              }`}
          >
            {/* Start */}
            <div className="flex border-b border-slate-200 dark:border-slate-700 w-full">
              <ul className="text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar __tutorial-order-modal_tab_nav">
                <li
                  className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                  onClick={() => {
                    if (tutorialRunning) {
                      return;
                    }
                    setCurrentView("order");
                  }}
                >
                  <span
                    className={
                      "cursor-pointer whitespace-nowrap flex items-center " +
                      (currentView === "order"
                        ? "text-indigo-500 dark:text-indigo-400"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                    }
                  >
                    <FaList
                      className="w-6 h-6
                   shrink-0 fill-current  mr-2"
                    />
                    <span>Order Details</span>
                  </span>
                </li>
                <CanView directOnly sauceyAdminAlwaysShow>
                  {order?.order_type === "delivery" && (
                    <li
                      className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                      onClick={() => {
                        if (tutorialRunning) {
                          return;
                        }
                        setCurrentView("notifications");
                      }}
                    >
                      <span
                        className={
                          "cursor-pointer whitespace-nowrap flex items-center " +
                          (currentView === "notifications"
                            ? "text-indigo-500 dark:text-indigo-400"
                            : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                        }
                      >
                        <FaBell
                          className="w-6 h-6
                   shrink-0 fill-current  mr-2"
                        />

                        <span className="mr-1">Notifications</span>
                        {notifications?.length > 0 && (
                          <Badge color="red">{notifications.length}</Badge>
                        )}
                      </span>
                    </li>
                  )}
                </CanView>
                {order?.order_type === "delivery" && (
                  <li
                    className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                    onClick={() => {
                      if (tutorialRunning) {
                        return;
                      }
                      setCurrentView("delivery");
                    }}
                  >
                    <span
                      className={
                        "cursor-pointer whitespace-nowrap flex items-center " +
                        (currentView === "delivery"
                          ? "text-indigo-500 dark:text-indigo-400"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                      }
                    >
                      {" "}
                      <FaTruck className="w-6 h-6 shrink-0 fill-current mr-2" />
                      <span>Delivery</span>
                    </span>
                  </li>
                )}
                <li
                  className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                  onClick={() => {
                    if (tutorialRunning) {
                      return;
                    }
                    setCurrentView("payment");
                  }}
                >
                  <span
                    className={
                      "cursor-pointer whitespace-nowrap flex items-center " +
                      (currentView === "payment"
                        ? "text-indigo-500 dark:text-indigo-400"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                    }
                  >
                    {" "}
                    <FaDollarSign className={"w-6 h-6 shrink-0 mr-2"} />
                    <span>Payment</span>
                  </span>
                </li>
                <li
                  className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                  onClick={() => {
                    if (tutorialRunning) {
                      return;
                    }
                    setCurrentView("history");
                  }}
                >
                  <span
                    className={
                      "cursor-pointer whitespace-nowrap flex items-center " +
                      (currentView === "history"
                        ? "text-indigo-500 dark:text-indigo-400"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                    }
                  >
                    <FaClock className={"w-6 h-6 shrink-0 mr-2"} />
                    <span>History</span>
                  </span>
                </li>
              </ul>
            </div>
            {/* End */}

            {currentView === "notifications" && (
              <div className="flex flex-col w-full pb-2">
                <table className="table-auto w-full dark:text-slate-300">
                  <thead className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <tr>
                      <th className="text-left">Type</th>
                      <th className="text-left max-w-2xl">Message</th>
                      <th className="text-left">Created</th>
                    </tr>
                  </thead>
                  {notifications.map((notification) => (
                    <tr key={notification.id} className="text-sm">
                      <td className="whitespace-nowrap capitalize">
                        {notification.state}
                      </td>
                      <td className="whitespace-nowrap">
                        {notification.message.replace(
                          new RegExp(`^.*${order.order_number}\:\\s*`),
                          ""
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        {moment(notification.created_at).format("lll")}
                      </td>
                    </tr>
                  ))}
                </table>
              </div>
            )}

            {currentView === "payment" && (
              <div className="flex flex-col w-full pb-2">
                <ModalBasic
                  width={"md"}
                  modalOpen={Boolean(viewPaymentIntentId)}
                  setModalOpen={setViewPaymentIntentId}
                  zIndex={60}
                >
                  <PaymentModal
                    paymentIntentID={viewPaymentIntentId}
                    setClose={() => setViewPaymentIntentId(null)}
                  />
                </ModalBasic>
                <table className="table-auto w-full dark:text-slate-300">
                  <thead className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <tr>
                      <th className="text-left">
                        {(() => {
                          if (order.payments?.[0]?.frame_charge_intent_id) {
                            return "Frame ID";
                          } else if (order.payments?.[0]?.payment_intent_id) {
                            return "Stripe ID";
                          } else if (order.payments?.[0]?.nmi_transaction_id) {
                            return "NMI ID";
                          }
                          return "Payment ID";
                        })()}
                      </th>
                      <th className="text-left">Amount</th>
                      <th className="text-left">Status</th>
                      <th className="text-left">Created</th>
                      <th className="text-left">View</th>
                    </tr>
                  </thead>
                  {order.payments?.map((payment) => (
                    <tr key={payment.id} className="text-sm">
                      <td className="whitespace-nowrap">
                        {payment.nmi_transaction_id ||
                          payment.payment_intent_id ||
                          payment.frame_refund_id?.slice(-10) ||
                          payment.frame_charge_intent_id?.slice(-10)}{" "}
                      </td>
                      <td className="whitespace-nowrap">
                        {formatValue(payment.hold_amount / 100)}
                      </td>
                      <td className="whitespace-nowrap">
                        {(() => {
                          switch (payment.charge_status) {
                            case -1:
                              return <Badge color="red">Refunded</Badge>;
                            case 1:
                              return <Badge color="yellow">Authorized</Badge>;
                            case 2:
                              return <Badge color="green">Captured</Badge>;
                          }
                        })()}
                      </td>
                      <td className="whitespace-nowrap">
                        {moment(payment.created_at).format("lll")}
                      </td>
                      <td className="whitespace-nowrap">
                        <SecondaryButton
                          naked
                          size={"sm"}
                          onClick={() => {
                            if (payment.payment_intent_id) {
                              setViewPaymentIntentId(payment.payment_intent_id);
                            } else if (payment.nmi_transaction_id) {
                              window.open(
                                `https://blvd.transactiongateway.com/merchants/reports.php?Action=Details&transaction_type=cc&merchant_id=${payment.nmi_gateway_id}&transaction=${payment.nmi_transaction_id}&report_id=0`
                              );
                            } else if (payment.frame_charge_intent_id) {
                              window.open(
                                `https://app.framepayments.com/transactions/${payment.frame_charge_intent_id}`,
                                "_blank"
                              );
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="icon icon-tabler icon-tabler-eye cursor-pointer stroke-current"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                          </svg>
                        </SecondaryButton>
                      </td>
                    </tr>
                  ))}
                </table>
              </div>
            )}
            {currentView === "history" && (
              <OrderHistoryTimeline
                orderId={order?._id || orderID}
                timezone={selectedLocation?.tz}
                isActive={currentView === "history" && modalOpen}
              />
            )}
            {currentView === "delivery" && (
              <>
                <div
                  className={`flex ${isSmallSize ? "flex-col" : "flex-row"
                    } justify-between w-full border-b border-slate-200 dark:border-slate-700 pb-2`}
                >
                  <div>
                    <div className="text-slate-400 dark:text-slate-500">
                      Customer
                    </div>
                    <div className=" text-slate-800 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <CanView directOnly sauceyAdminAlwaysShow>
                          <span
                            className="cursor-pointer underline font-semibold"
                            onClick={() => setCustomerModalOpen(true)}
                          >
                            {order.customer.first_name}{" "}
                            {order.customer.last_name}
                          </span>
                        </CanView>
                        {user.type !== "saucey_admin" && (
                          <CanView mpOnly>
                            <span className="font-semibold">
                              {order.customer.first_name}{" "}
                              {order.customer.last_name}
                            </span>
                          </CanView>
                        )}
                        <CanView directOnly sauceyAdminAlwaysShow>
                          <button
                            onClick={() => setCustomerModalOpen(true)}
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                            title="Open customer profile"
                          >
                            <FaArrowUpRightFromSquare className="w-3 h-3" />
                          </button>
                        </CanView>
                      </div>
                      {order.external_loyalty_program && (
                        <Badge color={"green"}>
                          {order.external_loyalty_program.program} Member
                        </Badge>
                      )}
                      <div>{formatPhoneNumber(order.customer.mobile)}</div>
                      <div>{order.customer.email}</div>
                      <div>{order.customer.birthday}</div>
                    </div>
                  </div>
                  <CanView permissions="saucey_admin">
                    <div>
                      <div className="text-slate-400 dark:text-slate-500">
                        Store Details
                      </div>
                      <div className="font-semibold text-slate-800 dark:text-slate-100">
                        <div>{order.vendor?.name}</div>
                        <div>{order.vendor?.address}</div>
                        <div>
                          {order.vendor?.city}, {order.vendor?.state}{" "}
                          {order.vendor?.zip}
                        </div>
                      </div>
                    </div>
                  </CanView>
                  <CanView directOnly sauceyAdminAlwaysShow>
                    <div>
                      <div className="text-slate-400 dark:text-slate-500">
                        Delivery Details
                      </div>
                      <div className="font-semibold text-slate-800 dark:text-slate-100">
                        <div>{order.delivery_address_detail?.address}</div>
                        <div>
                          {order.delivery_address_detail?.city},{" "}
                          {order.delivery_address_detail?.state}{" "}
                          {order.delivery_address_detail?.zip}
                        </div>
                      </div>
                    </div>
                  </CanView>
                </div>
                {order?.delivery_notes && (
                  <div className="justify-start items-start w-full border-b border-slate-200 dark:border-slate-700 pb-2">
                    <span className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
                      <FaComment className="" /> Customer Notes
                    </span>
                    <div className="text-sm">{order?.delivery_notes}</div>
                  </div>
                )}
                {order?.provider_delivery && (
                  <div className="justify-start items-start w-full pb-2 flex flex-row justify-between">
                    <div>
                      <div className="text-slate-400 dark:text-slate-500 text-md">
                        Tracking
                      </div>
                      <div className="text-sm flex flex-col">
                        {!order?.provider_delivery.delivery_tracking_url ? (
                          "No tracking available yet"
                        ) : (
                          <SecondaryButton
                            size={"sm"}
                            onClick={() => {
                              window.open(
                                order?.provider_delivery.delivery_tracking_url,
                                "_blank"
                              );
                            }}
                          >
                            Track on {order?.provider_delivery.provider?.name}
                          </SecondaryButton>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-400 dark:text-slate-500 text-md">
                        Status
                      </div>
                      <div className="text-sm flex flex-col capitalize">
                        {order?.provider_delivery?.status?.replace("_", " ")}
                      </div>
                    </div>
                    {order?.driver?.first_name && (
                      <div>
                        <div className="text-slate-400 dark:text-slate-500">
                          Courier
                        </div>
                        <div className="text-sm">
                          {order.driver.first_name} {order.driver.last_name}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {currentView === "order" && (
              <>
                <div
                  className={`flex ${isSmallSize ? "flex-col text-sm" : "flex-row"
                    } justify-between w-full border-b border-slate-200 dark:border-slate-700 pb-2`}
                >
                  <div>
                    {!isSmallSize && (
                      <div className="text-slate-400 dark:text-slate-500">
                        Customer
                      </div>
                    )}
                    <div className="text-slate-800 dark:text-slate-100">
                      <div className={"flex gap-1 items-center"}>
                        <div className="flex items-center gap-2">
                          <CanView directOnly sauceyAdminAlwaysShow>
                            <span
                              className="cursor-pointer underline font-semibold"
                              onClick={() => setCustomerModalOpen(true)}
                            >
                              {order.customer.first_name}{" "}
                              {order.customer.last_name}
                            </span>
                          </CanView>
                          {user.type !== "saucey_admin" && (
                            <CanView mpOnly>
                              <span className="font-semibold">
                                {order.customer.first_name}{" "}
                                {order.customer.last_name}
                              </span>
                            </CanView>
                          )}
                          <CanView directOnly sauceyAdminAlwaysShow>
                            <button
                              onClick={() => setCustomerModalOpen(true)}
                              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                              title="Open customer profile"
                            >
                              <FaArrowUpRightFromSquare className="w-3 h-3" />
                            </button>
                          </CanView>
                        </div>
                        {order.external_loyalty_program && (
                          <Badge slim color={"green"}>
                            {order.external_loyalty_program.program} Member
                          </Badge>
                        )}
                      </div>
                      <div>{formatPhoneNumber(order.customer.mobile)}</div>
                      <div>{order.customer.email}</div>
                      <CanView directOnly sauceyAdminAlwaysShow>
                        <div>
                          DOB: {order.customer.birthday}{" "}
                          {order.customer.verification_status === "PASSED" ? (
                            <Badge slim color={"green"}>
                              Verified
                            </Badge>
                          ) : (
                            <Badge slim color={"red"}>
                              Self-reported
                            </Badge>
                          )}
                        </div>
                      </CanView>
                      {order?.payment_card_last_4 && (
                        <CanView directOnly sauceyAdminAlwaysShow>
                          <div className="flex items-center gap-2">
                            Payment:
                            <img
                              src={GetCardIcon({
                                brand: order.payment_card_brand.toLowerCase(),
                              })}
                              alt={order.payment_card_brand}
                              className="h-5"
                            />
                            •••• {order.payment_card_last_4}
                          </div>
                        </CanView>
                      )}
                    </div>
                  </div>
                  <div className={isSmallSize ? "" : "text-right"}>
                    {!isSmallSize && (
                      <div className="text-slate-400 dark:text-slate-500">
                        {order.direct ? "Order Detail" : "Order Status"}
                      </div>
                    )}
                    <div className="font-semibold text-slate-800 dark:text-slate-100 capitalize">
                      <div>
                        <CanView directOnly>
                          {order.order_type || "delivery"}{" "}
                        </CanView>
                        <Badge
                          customColor={statusColor(
                            OrderStatusInverseEnum[order.order_status]
                          )}
                          size="xs"
                          slim
                        >
                          {(OrderStatusInverseEnum[order.order_status] || "")
                            .replace("_", " ")
                            .replace("IN PROGRESS", "EN ROUTE")}
                        </Badge>
                      </div>
                      {order.scheduled && (
                        <div>
                          Scheduled: {moment(order.scheduled).format("lll")}
                        </div>
                      )}
                      {order.fraud_eval && (
                        <CanView directOnly sauceyAdminAlwaysShow>
                          <div
                            className="flex items-center justify-end gap-2 cursor-pointer mt-1"
                            onClick={() => setViewFraudEval(order.fraud_eval)}
                          >
                            <Badge
                              color={order.fraud_eval.fraud_level}
                              size="xs"
                            >
                              <div className="flex items-center gap-2">
                                <FaMagnifyingGlass className="w-4 h-4" />
                                Fraud Score: {order.fraud_eval.score}
                                <svg
                                  className={
                                    "fill-current text-slate-400 dark:text-slate-500 w-4 h-4"
                                  }
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
                                </svg>
                              </div>
                            </Badge>
                          </div>
                        </CanView>
                      )}
                    </div>
                  </div>
                </div>
                {order.order_status === -1 && (
                  <>
                    {timeRemaining !== null && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 w-full">
                        <div className="flex items-center">
                          <div>
                            <h3 className="text-sm font-medium text-yellow-800">
                              Customer Decision Time Remaining
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p>
                                The customer has until{" "}
                                <strong>
                                  {formatTimeRemaining(timeRemaining)}
                                </strong>{" "}
                                to decide on alternatives.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {/*items*/}
                <div
                  className={
                    "w-full overflow-auto mb-4 min-h-[300px] max-h-[calc(100%-300px)]"
                  }
                >
                  {cart?.products.map((product, idx) => {
                    // Find matching product in order.products to get substitution_preference
                    const orderProduct = order?.products?.find(
                      (op) => op.sku_id === product.sku_id
                    );

                    let price;
                    let unitPrice;
                    let productDiscountPrice = 0;
                    let bulkPricing = null;
                    let bulkPricingDescription = null;
                    let bulkPricingDiscount = 0;
                    if (order.direct) {
                      price =
                        (product.qty ?? product.quantity) *
                        (product.discount_price ??
                          product.price ??
                          product.sell_price);

                      unitPrice = product.sell_price ?? product.price
                      productDiscountPrice = product.discounted_price
                    } else {
                      price = orderProduct.buy_price * orderProduct.quantity // non discounted products don't have retailer_subtotal, don't ask me why

                      unitPrice = orderProduct.buy_price
                      if (orderProduct.retailer_subtotal < price && orderProduct.package_qty > 1) {
                        bulkPricing = orderProduct.retailer_subtotal
                        bulkPricingDescription = `Buy ${orderProduct.package_qty} save ${formatValue((orderProduct.buy_price - orderProduct.package_buy_price) * orderProduct.package_qty)}`;
                        bulkPricingDiscount = orderProduct.buy_price * orderProduct.quantity - orderProduct.retailer_subtotal;
                      } else {
                        bulkPricing = null;
                        bulkPricingDescription = null;
                        bulkPricingDiscount = 0;
                      }

                    }

                    const rowColor =
                      idx % 2 === 0 ? "bg-slate-100 dark:bg-slate-700" : "";
                    return (
                      <div
                        className={`relative py-1 px-1 flex flex-row  w-full flex-1 ${rowColor} ${isSmallSize ? "text-sm" : ""
                          }`}
                        key={product.sku_id}
                      >
                        <div className="w-16 mr-4">
                          <img
                            src={product.image}
                            alt={product.title}
                            className="h-16 w-16 object-cover rounded"
                          />
                        </div>
                        <div
                          className={
                            "flex flex-grow justify-center flex-col" +
                            (isSmallSize ? " w-full" : " w-full ")
                          }
                        >
                          <div className="font-semibold text-slate-800 dark:text-slate-100 max-w-[260px] sm:max-w-[360px] md:max-w-[420px] lg:max-w-[520px] truncate">
                            {product.title} {product.subtitle}
                          </div>
                          <div className="text-slate-400 dark:text-slate-500">
                            {product.volume}
                          </div>
                          {bulkPricingDescription && (
                            <div className={"text-slate-400 dark:text-slate-500 " + (isSmallSize ? "" : "text-right")}>
                              <FaTag className="inline-block mr-1" />{bulkPricingDescription}
                              {isSmallSize && <span className="text-slate-400 dark:text-slate-500"> =
                                <span className="font-bold ml-1">
                                  -{formatValue(bulkPricingDiscount)}
                                </span></span>}
                            </div>
                          )}
                          {bulkPricingDiscount > 0 && !isSmallSize && (<div > &nbsp; </div>)}

                          {isSmallSize && (
                            <div className="text-slate-400 dark:text-slate-500">
                              {product.qty ?? product.quantity} @{" "}
                              <span
                                className={
                                  productDiscountPrice ? "line-through" : ""
                                }
                              >
                                {formatValue(
                                  unitPrice
                                )}
                              </span>
                              {Boolean(productDiscountPrice) && (
                                <span className="text-red-500">
                                  {" "}
                                  {formatValue(productDiscountPrice)}
                                </span>
                              )}{" "}
                              = <span className={`${bulkPricing ? "line-through" : "font-bold"} `}>{formatValue(price)}</span>
                              {bulkPricing && (
                                <span className="font-bold ml-1">
                                  {formatValue(bulkPricing)}
                                </span>
                              )}
                            </div>
                          )}
                          {/* {oosFlow && (
                        <div className="text-sm mt-1">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <span className="text-slate-600 dark:text-slate-400 whitespace-nowrap">Preference:</span>
                          <div className="flex items-center gap-1">
                            {orderProduct?.substitution_preference ? (
                              <Badge
                                color={
                                  orderProduct.substitution_preference === "REPLACE" ? "green" :
                                    orderProduct.substitution_preference === "REMOVE" ? "red" :
                                  orderProduct.substitution_preference === "CONTACT" ? "blue" : "gray"
                                }
                                size="sm"
                              >
                                {substitutionPreferenceMap[orderProduct.substitution_preference]}
                              </Badge>
                            ) : (
                              <Badge color="gray" size="sm">
                                NOT SET
                              </Badge>
                            )}
                            <Tooltip position="bottom" size="sm">
                              {orderProduct?.substitution_preference === "REPLACE" ? (
                                "Replace with similar item."
                              ) : orderProduct?.substitution_preference === "REMOVE" ? (
                                "Skip this item if unavailable."
                              ) : orderProduct?.substitution_preference === "CONTACT" ? (
                                "Suggest alternative options for customer to choose from."
                              ) : orderProduct?.substitution_preference ? (
                                "Unknown preference"
                              ) : (
                                "No substitution preference specified"
                              )}
                            </Tooltip>
                          </div>
                        </div>
                        </div>
                      )} */}
                          {oosFlow && (
                            <div className="mt-2 flex gap-2 flex-col sm:flex-row">
                              <div className="relative inline-block">
                                {hasOosSelection(product.sku_id) ? (
                                  <DangerButton
                                    size="xs"
                                    onClick={() =>
                                      handleOutOfStock(product, orderProduct)
                                    }
                                    width={isSmallSize ? "full" : undefined}
                                  >
                                    {(() => {
                                      const selection = loadOosSelection(
                                        product.sku_id
                                      );
                                      const orderedQuantity =
                                        product?.qty ?? product?.quantity ?? 0;
                                      const preference =
                                        orderProduct?.substitution_preference;

                                      if (
                                        selection.availableQuantity >=
                                        orderedQuantity
                                      ) {
                                        return `${orderedQuantity}/${orderedQuantity} available, fulfilling normally`;
                                      }

                                      switch (preference) {
                                        case "CONTACT":
                                          return `${selection.availableQuantity
                                            }/${orderedQuantity} available, recommended ${selection.selectedRecommendations
                                              ?.length || 0
                                            } alternatives`;

                                        case "REPLACE":
                                          const remaining =
                                            orderedQuantity -
                                            selection.availableQuantity;
                                          if (
                                            selection.availableQuantity === 0
                                          ) {
                                            return `0/${orderedQuantity} available, send ${remaining} substitution`;
                                          }
                                          return `${selection.availableQuantity}/${orderedQuantity} available, send ${selection.availableQuantity} original and ${remaining} substitution`;

                                        case "REMOVE":
                                          const toRemove =
                                            orderedQuantity -
                                            selection.availableQuantity;
                                          if (
                                            selection.availableQuantity === 0
                                          ) {
                                            return `0/${orderedQuantity} available, remove from order`;
                                          }
                                          return `${selection.availableQuantity}/${orderedQuantity} available, send ${selection.availableQuantity} and remove ${toRemove}`;

                                        default:
                                          return `${selection.availableQuantity}/${orderedQuantity} available`;
                                      }
                                    })()}
                                  </DangerButton>
                                ) : (
                                  <SecondaryButton
                                    size="xs"
                                    width={isSmallSize ? "full" : undefined}
                                    onClick={() =>
                                      handleOutOfStock(product, orderProduct)
                                    }
                                  >
                                    OUT OF STOCK
                                  </SecondaryButton>
                                )}
                                {hasOosSelection(product.sku_id) && (
                                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-grow justify-center flex-col text-right">
                          <div
                            className={`font-semibold text-slate-800 dark:text-slate-100 flex justify-end items-center ${isSmallSize ? "w-[2rem]" : "w-24"
                              }`}
                          >
                            {editingOrder ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    if (
                                      (product.qty ?? product.quantity) === 1
                                    ) {
                                      if (hasUnsavedChanges) {
                                        deleteItem(product.sku_id);
                                      } else {
                                        editQuantity(product.sku_id, 0);
                                      }
                                    } else {
                                      editQuantity(
                                        product.sku_id,
                                        (product.qty ?? product.quantity) - 1
                                      );
                                    }
                                  }}
                                  disabled={
                                    processing ||
                                    (product.qty ?? product.quantity) <= 0
                                  }
                                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${(product.qty ?? product.quantity) <= 0 ||
                                    processing
                                    ? "border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                                    : "border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:scale-105"
                                    }`}
                                >
                                  {(product.qty ?? product.quantity) === 1 ? (
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  )}
                                </button>
                                <div className="min-w-[50px] px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-center">
                                  <span className="text-lg font-bold text-slate-800 dark:text-slate-100">
                                    {product.qty ?? product.quantity}
                                  </span>
                                </div>

                                <button
                                  disabled={processing}
                                  onClick={() => {
                                    editQuantity(
                                      product.sku_id,
                                      (product.qty ?? product.quantity) + 1
                                    );
                                  }}
                                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${processing
                                    ? "border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                                    : "border-green-500 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 hover:scale-105"
                                    }`}
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <span
                                className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1.5 rounded-full bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 text-2xl font-extrabold tracking-wide shadow-sm"
                              >
                                {product.qty ?? product.quantity}
                              </span>
                            )}
                            {!isSmallSize &&
                              <span className="ml-1">
                                x
                                <span
                                  className={
                                    productDiscountPrice ? "line-through ml-1" : "ml-1"
                                  }
                                >
                                  {formatValue(
                                    unitPrice
                                  )}
                                </span>
                                {Boolean(productDiscountPrice) && (
                                  <span className="text-red-500">
                                    {" "}
                                    {formatValue(productDiscountPrice)}
                                  </span>
                                )}{" "}
                              </span>}
                          </div>

                          {!isSmallSize && (
                            <div className={`text-slate-400 dark:text-slate-500 ${bulkPricing ? "line-through" : "font-bold"}`}>
                              {isNaN(price) ? "-" : formatValue(price)}
                            </div>
                          )}
                          {!isSmallSize && bulkPricingDiscount > 0 && (
                            <div className="text-slate-400 dark:text-slate-500">
                              - {formatValue(bulkPricingDiscount)}
                            </div>
                          )}
                          {!isSmallSize && bulkPricing && (
                            <div className={`text-slate-400 dark:text-slate-500 font-bold`}>
                              {formatValue(bulkPricing)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {editingOrder && (
                    <div
                      className={
                        "relative py-1 px-1 flex flex-row  w-full flex-1 justify-center mt-4 "
                      }
                    >
                      {!addingProduct && (
                        <div
                          ref={addProductButton}
                          className="__tutorial-order-modal_add_product_button"
                        >
                          <SecondaryButton
                            onClick={() => {
                              setAddingProduct(true);
                            }}
                          >
                            Add Product
                          </SecondaryButton>
                        </div>
                      )}
                      <ModalBasic
                        width={"2xl"}
                        modalOpen={addingProduct}
                        title={"Choose product to add"}
                        setModalOpen={(v) => {
                          if (!v) {
                            setAddingProduct(false);
                          }
                        }}
                      >
                        <div
                          className={
                            "flex align-center flex-col text-center w-full h-[400px] __tutorial-order-modal_add_product_modal"
                          }
                        >
                          <InstantSearch
                            indexName={`${config.environment}_${selectedMerchant.easy_id}_products`}
                            searchClient={searchClient}
                          >
                            <Configure hitsPerPage={50} filters={filter} />
                            {/*element to auto scroll to when shown*/}
                            <div
                              className={`flex ${isSmallSize ? "flex-col" : "flex-row"
                                }`}
                            >
                              <div
                                className={`${isSmallSize ? "pb-2" : "w-[350px]"
                                  }`}
                              >
                                <CustomRefinementList
                                  isTutorial={order.tutorial}
                                />
                              </div>
                              <div>
                                <Autocomplete isTutorial={order.tutorial} />
                                <CustomHits
                                  order={order}
                                  addToCart={addToCart}
                                  editingOrder={editingOrder}
                                  setEditingOrder={setEditingOrder}
                                  setAddingProduct={setAddingProduct}
                                  isTutorial={order.tutorial}
                                />
                              </div>
                            </div>
                          </InstantSearch>
                        </div>
                      </ModalBasic>
                    </div>
                  )}
                  <div className={"flex flex-1 items-end flex-col"}>
                    {[...((order?.direct ? cart?.line_items : []) || []), ...(lineItems || [])].map(
                      (item) => {
                        if (!item.value) return null;
                        let classname = `flex`;
                        if (item.withholding === "detail") {
                          return;
                        }
                        if (item.bold) classname += ` font-bold`;


                        return (
                          <div key={"cart_" + item.title} className={classname}>
                            <div className="flex gap-2 items-center">
                              <CanView directOnly>
                                {item.withholding && (
                                  <Tooltip size={"xl"} position={"left"}>
                                    {withholding}
                                  </Tooltip>
                                )}
                              </CanView>
                              <CanView mpOnly>
                                {item.withholding && (
                                  <Tooltip size={"xl"} position={"left"}>
                                    <p>
                                      Includes Fees, Service, and Transaction
                                      Fees
                                    </p>
                                    {order.tax_withheld && (
                                      <p>
                                        Saucey is a marketplace facilitator and
                                        has reached a sales tax nexus in your
                                        state of business, therefore we are
                                        responsible for remitting sales tax on
                                        this purchase
                                      </p>
                                    )}
                                  </Tooltip>
                                )}
                                {item.tooltip && (
                                  <Tooltip size={"xl"} position={"left"}>
                                    {item.tooltip}
                                  </Tooltip>
                                )}
                              </CanView>
                              {item.admin && <FaCrown className="w-4 h-4 text-amber-500" />}
                              {item.title}
                            </div>
                            <div className="w-24 justify-end flex">
                              {formatValue(item.value)}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>

                <div className="flex w-full flex-1 __tutorial-order-modal_action_bar">
                  <CanView directOnly sauceyAdminAlwaysShow>
                    {!editingOrder && (
                      <SecondaryDangerButton
                        onClick={() => {
                          if (tutorialRunning) {
                            return;
                          }
                          setAdditionalModal({
                            action: () => { }, // Dummy action since we handle it in confirm button
                            title: "Cancel Order",
                            titleBarClassName: "bg-red-400",
                            holdToContinue: true,
                            danger: true,
                            content: null,
                          });
                        }}
                        disabled={processing}
                        loading={processing}
                      >
                        <div className="flex gap-1 items-center">
                          Cancel Order
                          <CanView mpOnly>
                            <FaCrown />
                          </CanView>
                        </div>
                      </SecondaryDangerButton>
                    )}
                  </CanView>
                  <div className="flex gap-2 w-full flex-1 justify-end">
                    {!editingOrder ? (
                      <>
                        {![-2, -1, 5].includes(order.order_status) &&
                          order.order_type !== "pickup" && (
                            <>
                              {/* Hide Force Complete Order button for direct orders with orange/red fraud level */}
                              {(order.order_status !== 0 ||
                                !(
                                  order.fraud_eval &&
                                  (order.fraud_eval.fraud_level === "orange" ||
                                    order.fraud_eval.fraud_level === "red")
                                )) && (
                                  <CanView directOnly>
                                    <DangerButton
                                      disabled={processing}
                                      loading={processing}
                                      additionalClasses="!bg-green-500"
                                      onClick={() => {
                                        const action2 = () => {
                                          setProcessing(true);
                                          if (tutorialRunning) {
                                            return;
                                          }
                                          _completeOrder(true)
                                        };
                                        const action = () => {
                                          if (tutorialRunning) {
                                            return;
                                          }
                                          setAdditionalModal({
                                            holdToContinue: true,
                                            action: action2,
                                            title: (
                                              <div
                                                className={
                                                  "flex gap-1 items-center"
                                                }
                                              >
                                                Force Complete Order
                                              </div>
                                            ),
                                            content: (
                                              <>
                                                <div>
                                                  <p>
                                                    Orders should not be forced to
                                                    complete manually under normal
                                                    circumstances. Only do this if
                                                    you are sure the order is
                                                    ready to be completed.
                                                  </p>
                                                  <p>
                                                    Are you sure you want to force
                                                    this transaction to complete
                                                    and charge customer's credit
                                                    card?
                                                  </p>
                                                </div>
                                              </>
                                            ),
                                          });
                                        };
                                        action();
                                      }}
                                    >
                                      <div className={"flex gap-1 items-center"}>
                                        Force Complete Order
                                      </div>
                                    </DangerButton>
                                  </CanView>
                                )}
                              <CanView permissions="saucey_admin" mpOnly>
                                <DangerButton
                                  disabled={processing}
                                  loading={processing}
                                  onClick={() => {
                                    if (tutorialRunning) {
                                      return;
                                    }
                                    const action2 = () => {
                                      setProcessing(true);
                                      _completeOrder()
                                    };
                                    const action = () => {
                                      setAdditionalModal({
                                        holdToContinue: true,
                                        action: action2,
                                        title: (
                                          <div
                                            className={
                                              "flex gap-1 items-center"
                                            }
                                          >
                                            <FaCrown /> Force Finalize Order
                                          </div>
                                        ),
                                        content: (
                                          <>
                                            <div>
                                              Force this transaction to complete
                                              as a Saucey Admin and charge
                                              customer's credit card?
                                            </div>
                                          </>
                                        ),
                                      });
                                    };
                                    action();
                                  }}
                                >
                                  <div className={"flex gap-1 items-center"}>
                                    {" "}
                                    Force Complete <FaCrown />
                                  </div>
                                </DangerButton>
                              </CanView>
                            </>
                          )}

                        {order.order_status === 1 &&
                          order.order_type === "pickup" && (
                            <>
                              <CanView directOnly sauceyAdminAlwaysShow>
                                <SecondaryButton
                                  disabled={processing}
                                  loading={processing}
                                  onClick={() => {
                                    if (tutorialRunning) {
                                      return;
                                    }
                                    setEditingOrder(
                                      JSON.parse(JSON.stringify(order))
                                    );
                                  }}
                                >
                                  <div className="flex gap-1 items-center">
                                    Edit Order
                                    <CanView mpOnly>
                                      <FaCrown />
                                    </CanView>
                                  </div>
                                </SecondaryButton>
                              </CanView>
                              <PrimaryButton
                                disabled={processing}
                                loading={processing}
                                onClick={() => {
                                  const action2 = () => {
                                    setProcessing(true);
                                    if (
                                      tutorialRunning &&
                                      tutorialStepIndex !== 4
                                    ) {
                                      return;
                                    }
                                    _completeOrder()
                                  };
                                  const action = () => {
                                    setAdditionalModal({
                                      action: action2,
                                      title: "Finalize Order",
                                      confirmDisabled: "ageConfirmed",
                                      holdToContinue: false,
                                      content: (
                                        <>
                                          <div>
                                            Close out order and charge
                                            customer's credit card?
                                          </div>
                                        </>
                                      ),
                                    });
                                  };
                                  setAdditionalModal({
                                    action: action,
                                    title: "Complete Order",
                                    confirmDisabled: "ageConfirmed",
                                    holdToContinue: false,
                                    content: ageModal,
                                  });
                                  setTimeout(() => {
                                    document.getElementById("dob")?.focus();
                                  }, 20);
                                }}
                              >
                                Complete
                              </PrimaryButton>
                            </>
                          )}
                        {oosFlow && (
                          <>
                            <SecondaryButton
                              onClick={() => {
                                clearOosState();
                              }}
                            >
                              Cancel
                            </SecondaryButton>
                            <PrimaryButton
                              onClick={() => {
                                const hasAnySelections =
                                  Object.keys(oosSelectionsMap).length > 0;
                                if (hasAnySelections) {
                                  setOosSummaryOpen(true);
                                } else {
                                  setOosFlow(false);
                                }
                              }}
                            >
                              Continue
                            </PrimaryButton>
                          </>
                        )}
                        {!oosFlow && order.order_status === 4 && (
                          <PrimaryButton
                            onClick={() => {
                              setAdditionalModal({
                                action: async () => {
                                  setProcessing(true);
                                  try {
                                    await markOrderAsReturned(orderID);
                                    dispatch(getActiveOrders(true));
                                    setModalOpen(false);
                                    dispatch(
                                      addToast({
                                        type: "success",
                                        message:
                                          "Order marked as returned successfully",
                                      })
                                    );
                                  } catch (error) {
                                    dispatch(
                                      addToast({
                                        type: "error",
                                        message:
                                          "Failed to mark order as returned",
                                      })
                                    );
                                  } finally {
                                    setProcessing(false);
                                  }
                                },
                                title: "Mark as returned",
                                content: (
                                  <>
                                    <div>Mark this order as returned?</div>
                                  </>
                                ),
                                holdToContinue: false,
                              });
                            }}
                            disabled={processing}
                            loading={processing}
                          >
                            Mark as returned
                          </PrimaryButton>
                        )}
                        {(order.order_status === 0 ||
                          order.order_status === 11) &&
                          !order.stripe_3ds_url &&
                          !oosFlow && (
                            <>
                              <CanView directOnly sauceyAdminAlwaysShow>
                                <div className="__tutorial-order-modal_edit_order_button">
                                  <SecondaryButton
                                    disabled={processing}
                                    loading={processing}
                                    onClick={() => {
                                      if (
                                        tutorialRunning &&
                                        tutorialStepIndex !== 4
                                      ) {
                                        return;
                                      } else if (
                                        tutorialRunning &&
                                        tutorialStepIndex === 4
                                      ) {
                                        tutorialNextStep();
                                      }
                                      setEditingOrder(
                                        JSON.parse(JSON.stringify(order))
                                      );
                                    }}
                                  >
                                    <div className="flex gap-1 items-center">
                                      Edit Order
                                      <CanView mpOnly>
                                        <FaCrown />
                                      </CanView>
                                    </div>
                                  </SecondaryButton>
                                </div>
                              </CanView>
                              <CanView mpOnly>
                                <DangerButton
                                  disabled={processing}
                                  loading={processing}
                                  onClick={() => {
                                    setOosFlow(true);
                                  }}
                                >
                                  Out of Stock
                                </DangerButton>
                              </CanView>
                              {/* Show Fraud Review button for direct orders with orange/red fraud level */}
                              {(order.direct || user.type === "saucey_admin") &&
                                order.fraud_eval &&
                                (order.fraud_eval.fraud_level === "orange" ||
                                  order.fraud_eval.fraud_level === "red") ? (
                                <PrimaryButton
                                  disabled={processing}
                                  loading={processing}
                                  onClick={() => {
                                    if (tutorialRunning) {
                                      return;
                                    }
                                    setViewFraudEval(order.fraud_eval);
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <FaMagnifyingGlass className="w-4 h-4" />
                                    Fraud Review
                                  </div>
                                </PrimaryButton>
                              ) : (
                                <div className="__tutorial-order-modal_accept_order_button">
                                  <PrimaryButton
                                    disabled={processing}
                                    loading={processing}
                                    onClick={() => {
                                      if (tutorialRunning && tutorialStepIndex !== 8) {
                                        return;
                                      }
                                      if (!order.direct) {
                                        setAdditionalModal({
                                          action: () => {
                                            setProcessing(true);
                                            if (order.tutorial) {
                                              dispatch({
                                                type: "ACCEPT_TUTORIAL_ORDER",
                                                payload: orderID,
                                              });
                                              setProcessing(false);
                                              setModalOpen(false);
                                            } else {
                                              acceptOrder(orderID).then(() => {
                                                setProcessing(false);
                                                setModalOpen(false);
                                              });
                                            }
                                          },
                                          title: "Accept Order",
                                          content: (
                                            <>
                                              <div>
                                                Confirm you have all items in
                                                stock? If you cannot fulfill any
                                                part of this order, tap cancel
                                                then choose Out Of Stock.
                                              </div>
                                            </>
                                          ),
                                        });
                                      } else {
                                        setProcessing(true);
                                        if (order.tutorial) {
                                          dispatch({
                                            type: "ACCEPT_TUTORIAL_ORDER",
                                            payload: orderID,
                                          });
                                          if (tutorialRunning && tutorialStepIndex === 8) {
                                            tutorialNextStep();
                                            const payload = { ...order };
                                            const payload2 = { ...order };
                                            payload.order_status = 2;
                                            payload2.order_status = 3;
                                            setTimeout(() => {
                                              dispatch({
                                                type: "TUTORIAL_PRODUCT_UPDATE",
                                                payload: { order_id: order._id, order: payload },
                                              });
                                            }, 5000)
                                            setTimeout(() => {
                                              dispatch({
                                                type: "TUTORIAL_PRODUCT_UPDATE",
                                                payload: { order_id: order._id, order: payload2 },
                                              });
                                            }, 10000)
                                          }
                                          setProcessing(false);
                                          setModalOpen(false);
                                        } else {
                                          acceptOrder(orderID).then(() => {
                                            setProcessing(false);
                                            setModalOpen(false);
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    Accept
                                  </PrimaryButton>
                                </div>
                              )}
                            </>
                          )}
                        {order.order_status === 0 && order.stripe_3ds_url && (
                          <DangerButton disabled>
                            DO NOT DISPATCH, AWAITING PAYMENT VERIFICATION
                          </DangerButton>
                        )}
                      </>
                    ) : (
                      <>
                        <SecondaryButton
                          loading={processing}
                          onClick={() => {
                            setHasUnsavedChanges(false);
                            setForceReload(Date.now());
                            setEditingOrder(false);
                          }}
                        >
                          Cancel
                        </SecondaryButton>
                        <PrimaryButton
                          loading={processing}
                          onClick={() => {
                            if (order.tutorial) {
                              if (tutorialRunning && tutorialStepIndex === 7) {
                                // Check if there are exactly 3 lemons (sku_name includes 'lemon', case-insensitive) in the cart
                                const lemonProducts = cart.products.filter(
                                  (p) => p.sku_id === "tutorial-lemon"
                                );
                                const totalLemons = lemonProducts.reduce(
                                  (sum, p) => sum + (parseInt(p.qty) || 0),
                                  0
                                );
                                if (
                                  totalLemons === 3 &&
                                  cart.products.length === 1
                                ) {
                                  tutorialNextStep();
                                  setHasUnsavedChanges(false);
                                  setForceReload(Date.now());
                                  setEditingOrder(false);
                                }
                              }

                              return;
                            }
                            if (cart?.products.length === 0) {
                              dispatch(
                                addToast({
                                  type: "error",
                                  message: "There are no items in this cart",
                                  timer: 5,
                                })
                              );
                              return;
                            }
                            if (
                              order.required_3ds &&
                              order.payments.filter(
                                (p) => p.charge_status === 1
                              )?.[0]?.hold_amount < Math.floor(cart.total * 100)
                            ) {
                              setShow3DSWarning(true);
                              return;
                            }
                            setProcessing(true);
                            saveOrder();
                          }}
                        >
                          Save Order
                        </PrimaryButton>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </ModalBasic>
      {/* Customer Detail Modal */}
      <ModalBasic
        width="2xl"
        modalOpen={Boolean(viewFraudEval)}
        setModalOpen={() => setViewFraudEval(null)}
        title="Fraud Evaluation Details"
      >
        {viewFraudEval && (
          <div className="space-y-6">
            {/* Overall Score and Decision */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Overall Assessment
                </h3>
                <div className="flex items-center gap-3">
                  <Badge color={viewFraudEval.fraud_level || "gray"}>
                    <div className="flex items-center gap-2">
                      <PiDetectiveBold className="w-5 h-5" />
                      Score: {viewFraudEval.score}
                    </div>
                  </Badge>
                  <Badge
                    color={
                      viewFraudEval.decision === "approve"
                        ? "green"
                        : viewFraudEval.decision === "hold"
                          ? "yellow"
                          : viewFraudEval.decision === "auto-rejected"
                            ? "red"
                            : "gray"
                    }
                  >
                    {((decision) => {
                      switch (decision) {
                        case "auto-rejected":
                          return "Auto-Rejected";
                        case "hold":
                          return "Hold";
                        case "approve":
                          return "Not Flagged";
                        default:
                          return (
                            decision.charAt(0).toUpperCase() + decision.slice(1)
                          );
                      }
                    })(viewFraudEval.decision)}
                  </Badge>
                </div>
              </div>

              {/* Risk Level Description */}
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {viewFraudEval.fraud_level === "green" && (
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Low Risk - Order appears legitimate</span>
                  </div>
                )}
                {viewFraudEval.fraud_level === "orange" && (
                  <div className="flex items-center gap-2">
                    <FaTriangleExclamation className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    <span>Medium Risk - Review recommended</span>
                  </div>
                )}
                {viewFraudEval.fraud_level === "red" && (
                  <div className="flex items-center gap-2">
                    <FaCircleXmark className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span>High Risk - Proceed with caution</span>
                  </div>
                )}
                {!viewFraudEval.fraud_level && (
                  <div className="flex items-center gap-2">
                    <FaCircleInfo className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span>Risk level not available</span>
                  </div>
                )}
              </div>

              {/* Decision Description */}
              <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                {viewFraudEval.decision === "approve" && (
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Order not flagged</span>
                  </div>
                )}
                {viewFraudEval.decision === "hold" && (
                  <div className="flex items-center gap-2">
                    <FaTriangleExclamation className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span>Order held for manual review</span>
                  </div>
                )}
                {viewFraudEval.decision === "auto-rejected" && (
                  <div className="flex items-center gap-2">
                    <FaCircleXmark className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span>Order automatically rejected due to fraud risk</span>
                  </div>
                )}
                {viewFraudEval.decision === "unavailable" && (
                  <div className="flex items-center gap-2">
                    <FaCircleInfo className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span>Fraud evaluation unavailable</span>
                  </div>
                )}
              </div>
            </div>

            {/* Violations */}
            {viewFraudEval.violations &&
              viewFraudEval.violations.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                    Detected Issues ({viewFraudEval.violations.length})
                  </h3>
                  <div className="space-y-3">
                    {viewFraudEval.violations.map((violation, index) => (
                      <div
                        key={index}
                        className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-2 h-2 rounded-full ${violation.score >= 8
                                ? "bg-red-500"
                                : violation.score >= 5
                                  ? "bg-yellow-500"
                                  : "bg-green-500"
                                }`}
                            ></div>
                            <span className="font-medium text-slate-800 dark:text-slate-100">
                              {violation.criterion
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                          <Badge
                            color={
                              violation.score >= 8
                                ? "red"
                                : violation.score >= 5
                                  ? "yellow"
                                  : "green"
                            }
                            size="sm"
                          >
                            Score: {violation.score}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {violation.reason}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* No Violations Message */}
            {(!viewFraudEval.violations ||
              viewFraudEval.violations.length === 0) && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-green-800 dark:text-green-200 font-medium">
                      No fraud indicators detected
                    </span>
                  </div>
                </div>
              )}

            {/* Disclaimer */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <FaTriangleExclamation className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">
                    Disclaimer
                  </h4>
                  <p className="text-yellow-800 dark:text-yellow-200 text-xs">
                    This fraud evaluation is a tool to assist in decision-making
                    and should not be the sole factor in determining order
                    legitimacy. Always use your best judgment and consider
                    additional factors when processing orders. Fraud patterns
                    and risk factors may change over time.{" "}
                    <strong>
                      You are solely responsible for the final decision
                      regarding order approval or rejection.
                    </strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons for Direct Orders with Orange/Red Fraud Level */}

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <SecondaryButton onClick={() => setViewFraudEval(null)}>
                Close
              </SecondaryButton>
              <SecondaryDangerButton
                onClick={() => {
                  setCancelReason("fraud");
                  setAdditionalModal({
                    action: () => { }, // Dummy action since we handle it in confirm button
                    title: "Cancel Order",
                    titleBarClassName: "bg-red-400",
                    holdToContinue: true,
                    danger: true,
                    content: null,
                  });
                }}
                disabled={processing}
                loading={processing}
              >
                <div className="flex gap-1 items-center">
                  Cancel Order
                  <CanView mpOnly>
                    <FaCrown />
                  </CanView>
                </div>
              </SecondaryDangerButton>

              <CanView directOnly sauceyAdminAlwaysShow>
                <DangerButton
                  disabled={processing}
                  loading={processing}
                  additionalClasses="!bg-green-500"
                  onClick={() => {
                    setAdditionalModal({
                      action: () => {
                        setProcessing(true);
                        completeOrder(orderID, true).then(() => {
                          setProcessing(false);
                          setModalOpen(false);
                        });
                      },
                      title: "Force Complete Order",
                      titleBarClassName: "bg-green-400",
                      content: (
                        <>
                          <div>
                            <p>
                              Orders should not be forced to complete manually
                              under normal circumstances. Only do this if you
                              are sure the order is ready to be completed.
                            </p>
                            <p>
                              Are you sure you want to force this transaction to
                              complete and charge customer's credit card?
                            </p>
                          </div>
                        </>
                      ),
                    });
                  }}
                >
                  Force Complete Order
                </DangerButton>
              </CanView>
              {(order.order_status === 0 ||
                (order.order_status === 11 && !order.stripe_3ds_url)) && (
                  <PrimaryButton
                    disabled={processing}
                    loading={processing}
                    onClick={() => {
                      setProcessing(true);
                      if (order.tutorial) {
                        dispatch({
                          type: "ACCEPT_TUTORIAL_ORDER",
                          payload: orderID,
                        });
                        setProcessing(false);
                        setModalOpen(false);
                      } else {
                        acceptOrder(orderID).then(() => {
                          setProcessing(false);
                          setModalOpen(false);
                        });
                      }
                    }}
                  >
                    Accept Order
                  </PrimaryButton>
                )}
            </div>
          </div>
        )}
      </ModalBasic>
      <ModalBasic
        width={"xl"}
        modalOpen={Boolean(additionalModal) && additionalModal !== "x"}
        setModalOpen={setAdditionalModal}
        title={additionalModal?.title}
        titleBarClassName={additionalModal?.titleBarClassName}
      >
        {additionalModal !== "x" && (
          <>
            <div className={"space-y-4"}>
              {additionalModal?.title === "Cancel Order" ? (
                <CancelModalContent
                  cancelReason={cancelReason}
                  setCancelReason={setCancelReason}
                  customCancelReason={customCancelReason}
                  setCustomCancelReason={setCustomCancelReason}
                  cancelError={cancelError}
                  setCancelError={setCancelError}
                />
              ) : (
                additionalModal?.content
              )}
            </div>

            <div className={"flex justify-end gap-4 py-4 align-center"}>
              {additionalModal?.title === "Complete Order" && (
                <>
                  {ageConfirmed ? (
                    <div className={"flex items-center text-green-500"}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Age Confirmed
                    </div>
                  ) : (
                    <div className={"flex items-center text-red-500"}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-red-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Underage
                    </div>
                  )}
                </>
              )}
              <SecondaryButton
                onClick={() => {
                  setAdditionalModal(false);
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryActionButton
                loading={processing}
                holdToContinue={additionalModal.holdToContinue}
                disabled={
                  processing ||
                  (additionalModal.confirmDisabled
                    ? !modalConfirmation[additionalModal.confirmDisabled]
                    : false)
                }
                onClick={async () => {
                  // Special validation for Cancel Order modal
                  if (additionalModal.title === "Cancel Order") {
                    if (!cancelReason) {
                      setCancelError("Please select a reason for cancellation");
                      return;
                    }

                    if (
                      cancelReason === "other" &&
                      !customCancelReason.trim()
                    ) {
                      setCancelError(
                        "Please specify the reason for cancellation"
                      );
                      return;
                    }

                    // Clear errors if validation passes
                    setCancelError("");

                    // Execute cancel order directly
                    setProcessing(true);

                    const finalReason =
                      cancelReason === "other"
                        ? customCancelReason.trim()
                        : cancelReason;

                    try {
                      await cancelOrder(orderID, finalReason);
                      if (finalReason === "fraud") {
                        await banCustomer(
                          order.customer._id,
                          "Fraud Order - " + order.order_number
                        );
                      }
                      setAdditionalModal(false);
                      setProcessing(false);

                      // Reset cancel reason form
                      resetCancelForm();

                      setTimeout(() => {
                        setModalOpen(false);
                      }, 250);
                    } catch (err) {
                      setAdditionalModal(false);
                      setProcessing(false);
                      let message = "An error occurred";
                      try {
                        const serverMessage = err.response?.data?.error;
                        if (serverMessage) message = serverMessage;
                      } catch { }
                      message += ", updates are NOT saved.";
                      dispatch(addToast({ type: "error", message: message }));
                    }
                    return;
                  }

                  additionalModal.action();
                }}
              >
                {additionalModal.actionText || "Confirm"}
              </PrimaryActionButton>
            </div>
          </>
        )}
      </ModalBasic>

      {/* Out of Stock Modal */}
      <ModalBasic
        width={isSmallSize ? "lg" : "6xl"}
        modalOpen={oosModalOpen}
        setModalOpen={setOosModalOpen}
        title={`Out of Stock - ${oosProduct?.title}${oosLoadingRecommendations ? " (Loading recommendations...)" : ""
          }`}
        zIndex={60}
      >
        <div className="space-y-6">
          {/* Product Info */}
          <div className="flex items-start gap-4">
            <img
              src={oosProduct?.image}
              alt={oosProduct?.title}
              className="h-16 w-16 object-cover rounded"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
                {oosProduct?.title} {oosProduct?.subtitle}
              </h3>
              <p className="text-slate-400 dark:text-slate-500">
                {oosProduct?.volume}
              </p>
              <div className="mt-2">
                <Badge
                  color={
                    oosOrderProduct?.substitution_preference === "REPLACE"
                      ? "green"
                      : oosOrderProduct?.substitution_preference === "REMOVE"
                        ? "red"
                        : oosOrderProduct?.substitution_preference === "CONTACT"
                          ? "blue"
                          : "gray"
                  }
                  size="sm"
                >
                  {oosOrderProduct?.substitution_preference
                    ? `Customer wants to ${substitutionPreferenceMap[
                    oosOrderProduct.substitution_preference
                    ]
                    }`
                    : "No preference set"}
                </Badge>
              </div>
            </div>
          </div>

          {/* Quantity Available */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6">
            <div className="text-center space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  How many do you have?
                </h3>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Customer ordered:{" "}
                  <span className="font-bold text-slate-800 dark:text-slate-200">
                    {oosProduct?.qty ?? oosProduct?.quantity}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() =>
                    setOosAvailableQuantity(
                      Math.max(0, oosAvailableQuantity - 1)
                    )
                  }
                  disabled={oosAvailableQuantity <= 0}
                  className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${oosAvailableQuantity <= 0
                    ? "border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                    : "border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:scale-105"
                    }`}
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                <div className="min-w-[80px] px-6 py-4 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-center">
                  <div className="text-3xl font-bold text-slate-800 dark:text-slate-100">
                    {oosAvailableQuantity}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    available
                  </div>
                </div>

                <button
                  onClick={() =>
                    setOosAvailableQuantity(
                      Math.min(
                        oosProduct?.qty ?? oosProduct?.quantity,
                        oosAvailableQuantity + 1
                      )
                    )
                  }
                  disabled={
                    oosAvailableQuantity >=
                    (oosProduct?.qty ?? oosProduct?.quantity)
                  }
                  className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${oosAvailableQuantity >=
                    (oosProduct?.qty ?? oosProduct?.quantity)
                    ? "border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                    : "border-green-500 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 hover:scale-105"
                    }`}
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <div className="text-center">
                {oosAvailableQuantity === 0 ? (
                  <span className="text-red-600 dark:text-red-400 font-medium">
                    Completely out of stock
                  </span>
                ) : oosAvailableQuantity <
                  (oosProduct?.qty ?? oosProduct?.quantity) ? (
                  <span className="text-orange-600 dark:text-orange-400 font-medium">
                    Partial availability
                  </span>
                ) : (
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    Fully available
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Info Note - only show if partial availability */}
          {oosAvailableQuantity > 0 &&
            oosAvailableQuantity <
            (oosProduct?.qty ?? oosProduct?.quantity) && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  <strong>Note:</strong> Customer will receive{" "}
                  {oosAvailableQuantity} item(s). The remaining{" "}
                  {(oosProduct?.qty ?? oosProduct?.quantity) -
                    oosAvailableQuantity}{" "}
                  will be handled based on their substitution preference.
                </p>
              </div>
            )}

          {/* Action Based on Preference */}
          {(oosOrderProduct?.substitution_preference === "CONTACT" ||
            oosOrderProduct?.substitution_preference === "REPLACE") && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                    {oosOrderProduct?.substitution_preference === "CONTACT"
                      ? "Customer wants alternatives. Select up to 5 recommendations:"
                      : "Customer wants replacement. Select 1 substitute:"}
                  </h4>
                  {oosOrderProduct?.substitution_preference === "CONTACT" && (
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {oosSelectedRecommendations.length}/5 selected
                    </span>
                  )}
                </div>

                {oosLoadingRecommendations ? (
                  <div className="flex items-center justify-center py-8">
                    <InlineLoader
                      message="Loading recommendations..."
                      minHeight="h-32"
                    />
                  </div>
                ) : oosOrderProduct?.substitution_preference === "CONTACT" ? (
                  <InstantSearch
                    indexName={`${config.environment}_products`}
                    searchClient={searchClient}
                  >
                    <Configure
                      hitsPerPage={20}
                      filters={`${selectedLocation.store_id}_price:>0 && ${selectedLocation.store_id}_qty:>0`}
                    />
                    <Autocomplete className="mb-4" />
                    <OosSearchHits
                      storeId={selectedLocation.store_id}
                      oosSkuId={oosProduct?.sku_id}
                      paginatedRecommendations={paginatedRecommendations}
                      totalPages={totalPages}
                      oosCurrentPage={oosCurrentPage}
                      setOosCurrentPage={setOosCurrentPage}
                      recommendations={recommendations}
                      isRecommendationSelected={isRecommendationSelected}
                      handleRecommendationSelect={handleRecommendationSelect}
                      oosSelectedRecommendations={oosSelectedRecommendations}
                      oosOrderProduct={oosOrderProduct}
                      getPriceDelta={getPriceDelta}
                    />

                    {/* Selected Items Summary */}
                    {oosSelectedRecommendations.length > 0 && (
                      <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <h5 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center justify-between">
                          <span>
                            Selected{" "}
                            {oosOrderProduct?.substitution_preference ===
                              "CONTACT"
                              ? "Recommendations"
                              : "Substitute"}
                          </span>
                          <span
                            className={`px-2 py-1 text-xs rounded font-medium ${oosSelectedRecommendations.length > 0
                              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                              }`}
                          >
                            {oosSelectedRecommendations.length}/
                            {getMaxSelections()}
                          </span>
                        </h5>
                        <div className="space-y-2">
                          {oosSelectedRecommendations.map((item) => {
                            const delta = getPriceDelta(item.price);
                            return (
                              <div
                                key={item.sku_id}
                                className="bg-white dark:bg-slate-700 rounded border overflow-hidden"
                              >
                                {/* Mobile Layout - Stack elements */}
                                <div className="sm:hidden">
                                  <div className="flex items-start gap-3 p-3">
                                    <img
                                      src={item.image}
                                      alt={item.title}
                                      className="w-12 h-12 object-cover rounded flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-slate-800 dark:text-slate-100 leading-tight">
                                        {item.title} {item.subtitle}
                                      </div>
                                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {item.company} • {item.volume_label}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() =>
                                        handleRecommendationSelect(item, false)
                                      }
                                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      title="Remove selection"
                                    >
                                      <svg
                                        className="w-5 h-5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="px-3 pb-3">
                                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded p-2">
                                      <div className="font-medium text-sm text-slate-800 dark:text-slate-100">
                                        ${item.price?.toFixed(2)}
                                      </div>
                                      {delta && (
                                        <div
                                          className={`text-xs px-2 py-1 rounded font-medium ${delta.isPositive
                                            ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                                            : delta.isNegative
                                              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                                              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                                            }`}
                                        >
                                          {delta.isPositive
                                            ? "+"
                                            : delta.isNegative
                                              ? "-"
                                              : ""}
                                          ${delta.amount.toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Desktop Layout - Side by side */}
                                <div className="hidden sm:flex items-center justify-between p-3 min-h-[60px]">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <img
                                      src={item.image}
                                      alt={item.title}
                                      className="w-12 h-12 object-cover rounded flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-slate-800 dark:text-slate-100 leading-tight">
                                        {item.title} {item.subtitle}
                                      </div>
                                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {item.company} • {item.volume_label}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <div className="font-medium text-sm text-slate-800 dark:text-slate-100">
                                        ${item.price?.toFixed(2)}
                                      </div>
                                      {delta && (
                                        <div
                                          className={`text-xs px-2 py-1 rounded font-medium ${delta.isPositive
                                            ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                                            : delta.isNegative
                                              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                                              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                                            }`}
                                        >
                                          {delta.isPositive
                                            ? "+"
                                            : delta.isNegative
                                              ? "-"
                                              : ""}
                                          ${delta.amount.toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleRecommendationSelect(item, false)
                                    }
                                    className="ml-3 p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                                    title="Remove selection"
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </InstantSearch>
                ) : (
                  <>
                    {/* REPLACE preference - inline grid without search */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {paginatedRecommendations.map((item) => {
                        const isSelected = isRecommendationSelected(item);
                        return (
                          <div
                            key={item.sku_id}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${isSelected
                              ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                              : "border-slate-300 dark:border-slate-600 hover:border-green-400 hover:bg-slate-50 dark:hover:bg-slate-700"
                              }`}
                            onClick={() => {
                              handleRecommendationSelect(item, !isSelected);
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <img
                                src={item.image}
                                alt={item.title}
                                className="w-12 h-12 object-cover rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">
                                  {item.title} {item.subtitle}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {item.company}
                                </div>
                                <div className="text-xs text-slate-600 dark:text-slate-300">
                                  {item.volume_label} • {item.default_category}
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-green-600 dark:text-green-400 text-sm">
                                    ${item.price?.toFixed(2)}
                                  </div>
                                  {(() => {
                                    const delta = getPriceDelta(item.price);
                                    if (!delta) return null;
                                    return (
                                      <div
                                        className={`text-xs px-1 py-0.5 rounded ${delta.isPositive
                                          ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                                          : delta.isNegative
                                            ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                                            : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                                          }`}
                                      >
                                        {delta.isPositive ? "+" : delta.isNegative ? "-" : ""}
                                        ${delta.amount.toFixed(2)}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="text-green-500">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Selected Items Summary for REPLACE */}
                    {oosSelectedRecommendations.length > 0 && (
                      <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <h5 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center justify-between">
                          <span>Selected Substitute</span>
                          <span className="px-2 py-1 text-xs rounded font-medium bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                            {oosSelectedRecommendations.length}/{getMaxSelections()}
                          </span>
                        </h5>
                        <div className="space-y-2">
                          {oosSelectedRecommendations.map((item) => {
                            const delta = getPriceDelta(item.price);
                            return (
                              <div
                                key={item.sku_id}
                                className="bg-white dark:bg-slate-700 rounded border overflow-hidden"
                              >
                                <div className="flex items-center justify-between p-3 min-h-[60px]">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <img
                                      src={item.image}
                                      alt={item.title}
                                      className="w-12 h-12 object-cover rounded flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-slate-800 dark:text-slate-100 leading-tight">
                                        {item.title} {item.subtitle}
                                      </div>
                                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {item.company} • {item.volume_label}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <div className="font-medium text-sm text-slate-800 dark:text-slate-100">
                                        ${item.price?.toFixed(2)}
                                      </div>
                                      {delta && (
                                        <div
                                          className={`text-xs px-2 py-1 rounded font-medium ${delta.isPositive
                                            ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                                            : delta.isNegative
                                              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                                              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                                            }`}
                                        >
                                          {delta.isPositive ? "+" : delta.isNegative ? "-" : ""}
                                          ${delta.amount.toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleRecommendationSelect(item, false)
                                    }
                                    className="ml-3 p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                                    title="Remove selection"
                                  >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                      <path
                                        fillRule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 pt-4">
                        <button
                          onClick={() =>
                            setOosCurrentPage(Math.max(0, oosCurrentPage - 1))
                          }
                          disabled={oosCurrentPage === 0}
                          className={`px-3 py-1 rounded text-sm ${oosCurrentPage === 0
                            ? "text-slate-400 cursor-not-allowed"
                            : "text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            }`}
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-sm text-slate-600 dark:text-slate-400">
                          Page {oosCurrentPage + 1} of {totalPages}
                        </span>
                        <button
                          onClick={() =>
                            setOosCurrentPage(
                              Math.min(totalPages - 1, oosCurrentPage + 1)
                            )
                          }
                          disabled={oosCurrentPage >= totalPages - 1}
                          className={`px-3 py-1 rounded text-sm ${oosCurrentPage >= totalPages - 1
                            ? "text-slate-400 cursor-not-allowed"
                            : "text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            }`}
                        >
                          Next
                        </button>
                      </div>
                    )}

                    {recommendations.length === 0 && (
                      <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                        No recommendations available for this item.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          {oosOrderProduct?.substitution_preference === "REMOVE" && (
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 dark:text-red-200">
                  Customer wants to skip this item
                </h4>
                <p className="text-red-600 dark:text-red-300 text-sm mt-1">
                  This item will be removed from the order and the customer will
                  be refunded.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton
              onClick={() => {
                clearOosState();
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleConfirmOOS}
              disabled={
                oosLoadingRecommendations ||
                (oosOrderProduct?.substitution_preference === "CONTACT" &&
                  oosSelectedRecommendations.length === 0) ||
                (oosOrderProduct?.substitution_preference === "REPLACE" &&
                  oosSelectedRecommendations.length === 0)
              }
            >
              Confirm Out of Stock
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>

      {/* OOS Summary Modal */}
      <ModalBasic
        width="4xl"
        modalOpen={oosSummaryOpen}
        setModalOpen={setOosSummaryOpen}
        title="Review Out of Stock Items"
        zIndex={65}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-blue-400 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
                  Review Your Selections
                </h4>
                <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                  Please review your out of stock item selections below. This
                  information will be sent to the customer.
                </p>
              </div>
            </div>
          </div>

          {/* Summary Items */}
          <div className="space-y-4">
            {Object.keys(oosSelectionsMap).map((skuId) => {
              const selection = oosSelectionsMap[skuId];
              const selectedProduct = order?.products?.find(
                (p) => p.sku_id === skuId
              );

              if (!selection || !selectedProduct) return null;

              const originalQuantity =
                selectedProduct?.qty ?? selectedProduct?.quantity ?? 0;
              const isFullyAvailable =
                selection.availableQuantity >= originalQuantity;
              const isCompletelyOut = selection.availableQuantity === 0;
              const quantityStatus = isFullyAvailable
                ? "Fully available"
                : isCompletelyOut
                  ? "Completely out of stock"
                  : `Partial availability (${selection.availableQuantity}/${originalQuantity})`;

              return (
                <div
                  key={skuId}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <img
                      src={
                        selectedProduct.image || "/images/default-product.png"
                      }
                      alt={selectedProduct.title}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h5 className="font-semibold text-slate-800 dark:text-slate-100">
                        {selectedProduct.title} {selectedProduct.subtitle}
                      </h5>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {selectedProduct.brand} • {selectedProduct.volume}
                      </p>
                      <div className="mt-2 space-y-2">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded ${isFullyAvailable
                            ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                            : isCompletelyOut
                              ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                              : "bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400"
                            }`}
                        >
                          {quantityStatus}
                        </span>

                        {/* Explanation text */}
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                          {isFullyAvailable ? (
                            <span className="text-green-600 dark:text-green-400">
                              This item will be fulfilled normally.
                            </span>
                          ) : isCompletelyOut ? (
                            <span className="text-red-600 dark:text-red-400">
                              This item is out of stock,{" "}
                              {((preference) => {
                                switch (preference) {
                                  case "REPLACE":
                                    return "the customer will receive the substitute shown below.";
                                  case "CONTACT":
                                    return "the customer will be contacted to choose from alternative options.";
                                  case "REMOVE":
                                    return "and it will be removed from the order.";
                                }
                              })(selectedProduct.substitution_preference)}
                            </span>
                          ) : (
                            <span className="text-orange-600 dark:text-orange-400">
                              Only {selection.availableQuantity} of{" "}
                              {originalQuantity} units are available. The
                              customer will receive{" "}
                              {selection.availableQuantity},{" "}
                              {((preference) => {
                                switch (preference) {
                                  case "REPLACE":
                                    return `and the remaining ${originalQuantity -
                                      selection.availableQuantity
                                      } will be fulfilled with the substitute shown below.`;
                                  case "CONTACT":
                                    return `and will be contacted to choose an alternative for the remaining ${originalQuantity -
                                      selection.availableQuantity
                                      }.`;
                                  case "REMOVE":
                                    return `and the remaining ${originalQuantity -
                                      selection.availableQuantity
                                      } will be removed from the order.`;
                                }
                              })(selectedProduct.substitution_preference)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const newSelections = { ...oosSelectionsMap };
                        delete newSelections[skuId];
                        setOosSelectionsMap(newSelections);
                      }}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Remove this selection"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Substitution Details */}
                  {selection.substitutedProduct && (
                    <div className="ml-16 pl-3 border-l-2 border-green-200 dark:border-green-800">
                      <div className="text-sm">
                        <span className="font-medium text-green-700 dark:text-green-400">
                          Substitute:{" "}
                        </span>
                        <span className="text-green-600 dark:text-green-300">
                          {selection.substitutedProduct.title}{" "}
                          {selection.substitutedProduct.subtitle} (
                          {selection.substitutedProduct.volume})
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Recommendations Details - Only show for CONTACT preferences and when no substituted product */}
                  {!selection.substitutedProduct &&
                    selection.selectedRecommendations?.length > 0 && (
                      <div className="ml-16 pl-3 border-l-2 border-blue-200 dark:border-blue-800">
                        <div className="text-sm mb-2">
                          <span className="font-medium text-blue-700 dark:text-blue-400">
                            Recommendations (
                            {selection.selectedRecommendations.length}):
                          </span>
                        </div>
                        <div className="space-y-1">
                          {selection.selectedRecommendations.map(
                            (rec, index) => (
                              <div
                                key={rec.sku_id}
                                className="text-sm text-blue-600 dark:text-blue-300"
                              >
                                {index + 1}. {rec.title} {rec.subtitle} (
                                {rec.volume}) - ${rec.price?.toFixed(2)}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              );
            })}
          </div>

          {Object.keys(oosSelectionsMap).length === 0 && (
            <div className="text-center py-8">
              <div className="text-slate-500 dark:text-slate-400">
                No out of stock selections to review.
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton
              onClick={() => {
                setOosSummaryOpen(false);
                // Keep OOS flow active so they can edit selections
              }}
            >
              Edit Selections
            </SecondaryButton>
            <PrimaryButton
              onClick={handleFinalSubmission}
              disabled={Object.keys(oosSelectionsMap).length === 0}
              loading={processing}
            >
              Submit & Continue
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>
      <CustomerDetailModal
        modalOpen={customerModalOpen}
        setModalOpen={setCustomerModalOpen}
        customerEmail={order?.customer?.email}
        title="Customer Profile"
      />
    </div>
  );
};

const OosSearchHits = ({
  storeId,
  oosSkuId,
  paginatedRecommendations,
  totalPages,
  oosCurrentPage,
  setOosCurrentPage,
  recommendations,
  isRecommendationSelected,
  handleRecommendationSelect,
  oosSelectedRecommendations,
  oosOrderProduct,
  getPriceDelta,
}) => {
  const { hits } = useHits();
  const { query } = useSearchBox();
  const isSearching = query && query.trim().length > 0;

  const normalizedHits = useMemo(() => {
    if (!isSearching) return [];
    return hits
      .filter((hit) => hit.sku_id !== oosSkuId)
      .map((hit) => ({
        ...hit,
        price: hit[`${storeId}_price`],
      }));
  }, [hits, isSearching, oosSkuId, storeId]);

  const items = isSearching ? normalizedHits : paginatedRecommendations;

  return (
    <>
      {/* Recommendations / Search Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => {
          const isSelected = isRecommendationSelected(item);
          const canSelect =
            oosOrderProduct?.substitution_preference === "CONTACT"
              ? oosSelectedRecommendations.length < 5 || isSelected
              : true;

          return (
            <div
              key={item.sku_id}
              className={`p-3 border rounded-lg cursor-pointer transition-all ${isSelected
                ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                : canSelect
                  ? "border-slate-300 dark:border-slate-600 hover:border-green-400 hover:bg-slate-50 dark:hover:bg-slate-700"
                  : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 opacity-50 cursor-not-allowed"
                }`}
              onClick={() => {
                if (canSelect) {
                  handleRecommendationSelect(item, !isSelected);
                }
              }}
            >
              <div className="flex items-start gap-3">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-12 h-12 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">
                    {item.title} {item.subtitle}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {item.company}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    {item.volume_label} • {item.default_category}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-green-600 dark:text-green-400 text-sm">
                      ${item.price?.toFixed(2)}
                    </div>
                    {(() => {
                      const delta = getPriceDelta(item.price);
                      if (!delta) return null;

                      return (
                        <div
                          className={`text-xs px-1 py-0.5 rounded ${delta.isPositive
                            ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                            : delta.isNegative
                              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                            }`}
                        >
                          {delta.isPositive
                            ? "+"
                            : delta.isNegative
                              ? "-"
                              : ""}
                          ${delta.amount.toFixed(2)}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {isSelected && (
                  <div className="text-green-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination - only when not searching */}
      {!isSearching && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() =>
              setOosCurrentPage(Math.max(0, oosCurrentPage - 1))
            }
            disabled={oosCurrentPage === 0}
            className={`px-3 py-1 rounded text-sm ${oosCurrentPage === 0
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
          >
            Previous
          </button>

          <span className="px-3 py-1 text-sm text-slate-600 dark:text-slate-400">
            Page {oosCurrentPage + 1} of {totalPages}
          </span>

          <button
            onClick={() =>
              setOosCurrentPage(
                Math.min(totalPages - 1, oosCurrentPage + 1)
              )
            }
            disabled={oosCurrentPage >= totalPages - 1}
            className={`px-3 py-1 rounded text-sm ${oosCurrentPage >= totalPages - 1
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
          >
            Next
          </button>
        </div>
      )}

      {/* Search results count */}
      {isSearching && (
        <div className="text-center pt-2 text-sm text-slate-500 dark:text-slate-400">
          {normalizedHits.length} search result{normalizedHits.length !== 1 ? "s" : ""} found
        </div>
      )}

      {/* Empty state */}
      {!isSearching && recommendations.length === 0 && (
        <div className="text-center py-8 text-slate-500 dark:text-slate-400">
          No recommendations available for this item.
        </div>
      )}
    </>
  );
};

const CustomHits = ({
  order,
  addToCart,
  editingOrder,
  setEditingOrder,
  setAddingProduct,
  isTutorial = false,
}) => {
  const isSmallSize = useSmallSize();
  const { hits } = useHits();
  const [localHits, setLocalHits] = useState([]);

  useEffect(() => {
    if (isTutorial) {
      const items = [
        {
          sku_id: "tutorial-lime",
          title: "A tutorial Lime",
          volume_label: "Each",
          image: "https://cdn.saucey.com/direct/web/tutorial/lime.jpg",
          [order.store_id + "_price"]: 3.32,
        },
        {
          sku_id: "tutorial-lemon",
          title: "A tutorial Lemon",
          volume_label: "Each",
          image: "https://cdn.saucey.com/direct/web/tutorial/lemon.jpg",
          [order.store_id + "_price"]: 3.32,
        },
      ];
      // Only include items where sku_id is not already in order.products
      const existingSkuIds = (order?.products || []).map((p) => p.sku_id);
      const filteredItems = items.filter(
        (item) => !existingSkuIds.includes(item.sku_id)
      );
      setLocalHits(filteredItems);
      return;
    }
    const seen = {};
    const newHits = [];
    hits.forEach((hit) => {
      if (!seen[hit.sku_id]) {
        seen[hit.sku_id] = true;
        newHits.push(hit);
      }
    });

    setLocalHits(newHits);
  }, [hits, isTutorial]);

  return (
    // hightlight hovered
    // finger pointer
    <>
      {localHits.map((hit) => {
        return (
          <div
            className={`flex w-full justify-between gap-1 my-1 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer hitsItemTest ${isSmallSize ? "text-sm" : ""
              }`}
            onClick={() => {
              addToCart(hit);
              const newOrder = JSON.parse(JSON.stringify(editingOrder));
              newOrder.products.push({
                sku_id: hit.sku_id,
                title: hit.title,
                subtitle: hit.subtitle,
                volume: hit.volume_label,
                image: hit.image,
                sell_price: hit[order.store_id + "_price"],
                quantity: 1,
              });
              setEditingOrder(newOrder);
              setAddingProduct(false);
            }}
          >
            <img
              src={hit.image}
              alt={hit.title}
              className="h-16 w-16 object-cover rounded"
            />

            <div className="flex flex-row flex-grow justify-start flex-col items-start">
              <div className="font-semibold text-slate-800 dark:text-slate-100 text-left">
                {hit.title} {hit.subtitle}
              </div>
              <div className="text-slate-400 dark:text-slate-500">
                {hit.volume_label}
              </div>
            </div>
            <div className="flex flex-grow justify-center flex-col text-right">
              <div className="font-semibold text-slate-800 dark:text-slate-100">
                {formatValue(hit[order.store_id + "_price"])}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
export default OrderModal;
